import { useContext, useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Stack, 
  Paper, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  Tooltip,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  InputAdornment,
  FormGroup,
  Collapse,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import LoupeIcon from '@mui/icons-material/Loupe';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BusinessIcon from '@mui/icons-material/Business';
import ImportISLP from './ImportISLP';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ThemeContext } from '../Context';
import { LeftPanelContext } from '../Context';
import { useIndiceInfos } from '../tool/GetIndiceInfos';
import axios from 'axios';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';

function Elements() {
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  const { theme } = useContext(ThemeContext);
  const { highlightTarget } = useContext(LeftPanelContext);
  const { getIndiceData } = useIndiceInfos();
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Surbrillance temporaire des boutons Ajouter/Modifier, déclenchée par le guide de démarrage
  const [guideHighlight, setGuideHighlight] = useState(false);

  useEffect(() => {
    if (!highlightTarget || highlightTarget.view !== 'elements') return;
    setGuideHighlight(true);
    const timer = setTimeout(() => setGuideHighlight(false), 4000);
    return () => clearTimeout(timer);
  }, [highlightTarget]);

  // À spreader dans les boutons d'en-tête de section pendant la surbrillance
  const highlightSx = guideHighlight ? {
    backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(25, 118, 210, 0.08)',
    animation: 'guidePulse 1s ease-in-out infinite',
    '@keyframes guidePulse': {
      '0%, 100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
      '50%': {
        boxShadow: isDark
          ? '0 0 0 5px rgba(79, 195, 247, 0.35)'
          : '0 0 0 5px rgba(25, 118, 210, 0.3)'
      }
    }
  } : {};
  
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  // États pour la boîte de dialogue de modification d'unité
  const [openUnitDialog, setOpenUnitDialog] = useState(false);
  const [unit, setUnit] = useState('');
  const [unitError, setUnitError] = useState('');

  // États pour la boîte de dialogue de rédaction
  const [openRedactionDialog, setOpenRedactionDialog] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [redactionErrors, setRedactionErrors] = useState({});

  // États pour les intervenants
  const [openIntervenantDialog, setOpenIntervenantDialog] = useState(false);
  const [intervenantMode, setIntervenantMode] = useState('add');
  const [intervenantItemId, setIntervenantItemId] = useState(null);
  const [isExternalMember, setIsExternalMember] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isSignataire, setIsSignataire] = useState(false);
  const [isOPJ, setIsOPJ] = useState(false);
  const [isOPA, setIsOPA] = useState(false);
  const [rank, setRank] = useState('');
  const [lastname, setLastname] = useState('');
  const [firstname, setFirstname] = useState('');
  const [intervenantErrors, setIntervenantErrors] = useState({});

  // États pour les personnes concernées
  const [openPersonneDialog, setOpenPersonneDialog] = useState(false);
  const [personneMode, setPersonneMode] = useState('add');
  const [personneItemId, setPersonneItemId] = useState(null);
  const [personneQualite, setPersonneQualite] = useState('');
  const [additionalQualities, setAdditionalQualities] = useState([]);
  const [isUnknown, setIsUnknown] = useState(false);
  const [personneLastname, setPersonneLastname] = useState('');
  const [personneFirstname, setPersonneFirstname] = useState('');
  const [otherFirstnames, setOtherFirstnames] = useState('');
  const [gender, setGender] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [civilStatus, setCivilStatus] = useState('');
  const [nationality, setNationality] = useState('');
  const [profession, setProfession] = useState('');
  const [residence, setResidence] = useState('');
  const [nationalNumber, setNationalNumber] = useState('');
  const [raavisNumber, setRaavisNumber] = useState('');
  const [photographies, setPhotographies] = useState('');
  const [empreintes, setEmpreintes] = useState('');
  const [noticeIndividuelle, setNoticeIndividuelle] = useState('');
  const [personneErrors, setPersonneErrors] = useState({});

  // États pour les objets
  const [openObjetDialog, setOpenObjetDialog] = useState(false);
  const [objetMode, setObjetMode] = useState('add');
  const [objetItemId, setObjetItemId] = useState(null);
  const [description, setDescription] = useState('');
  const [sinNumber, setSinNumber] = useState('');
  const [objetErrors, setObjetErrors] = useState({});

  // États pour les véhicules
  const [openVehiculeDialog, setOpenVehiculeDialog] = useState(false);
  const [vehiculeMode, setVehiculeMode] = useState('add');
  const [vehiculeItemId, setVehiculeItemId] = useState(null);
  const [brand, setBrand] = useState('');
  const [vehiculeCategory, setVehiculeCategory] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [chassisNumber, setChassisNumber] = useState('');
  const [vehiculeNationality, setVehiculeNationality] = useState('');
  const [vehiculeColor, setVehiculeColor] = useState('');
  const [insurance, setInsurance] = useState('');
  const [technicalControl, setTechnicalControl] = useState('');
  const [vehiculeErrors, setVehiculeErrors] = useState({});

  // États pour les personnes morales
  const [openPersonneMoraleDialog, setOpenPersonneMoraleDialog] = useState(false);
  const [personneMoraleMode, setPersonneMoraleMode] = useState('add');
  const [personneMoraleItemId, setPersonneMoraleItemId] = useState(null);
  const [personneMoraleQualite, setPersonneMoraleQualite] = useState('');
  const [denomination, setDenomination] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [headquarters, setHeadquarters] = useState('');
  const [personneMoraleErrors, setPersonneMoraleErrors] = useState({});

  // Liste des qualités disponibles pour les personnes morales (avec possibilité d'ajout dynamique)
  const [personneMoraleQualitesList, setPersonneMoraleQualitesList] = useState([
    'Préjudicié',
    'Autre'
  ]);

  // États pour les faits
  const [openFaitDialog, setOpenFaitDialog] = useState(false);
  const [faitMode, setFaitMode] = useState('add');
  const [faitItemId, setFaitItemId] = useState(null);
  const [isUndetermined, setIsUndetermined] = useState(false);
  const [isQualified, setIsQualified] = useState(false);
  const [isTentative, setIsTentative] = useState(false);
  const [fait, setFait] = useState('');
  const [article, setArticle] = useState('');
  const [timeType, setTimeType] = useState('fixed');
  const [fixedDateTime, setFixedDateTime] = useState('');
  const [rangeLowDateTime, setRangeLowDateTime] = useState('');
  const [rangeHighDateTime, setRangeHighDateTime] = useState('');
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [destination, setDestination] = useState('');
  const [faitErrors, setFaitErrors] = useState({});
  
  // État pour la confirmation de suppression
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  
  // État pour la notification de copie
  const [copyNotification, setCopyNotification] = useState(false);
  const [copiedText, setCopiedText] = useState('');
  const [openImportISLP, setOpenImportISLP] = useState(false);
  
  // Référence pour suivre les dernières actions afin d'éviter les doublons
  const lastActionRef = useRef({
    type: null,
    id: null,
    timestamp: null
  });

  // Liste des grades disponibles
  const gradesList = [
    'Aspirant Agent de Police',
    'Agent de Police',
    'Premier Agent de Police',
    'Aspirant Agent de sécurisation de Police',
    'Agent de sécurisation de Police',
    'Premier Agent de sécurisation de Police',
    'Aspirant Assistant de Sécurisation de Police',
    'Assistant de Sécurisation de Police',
    'Premier Assistant de Sécurisation de Police',
    'Coordonnateur de Sécurisation de Police',
    'Premier Coordonnateur de Sécurisation de Police',
    'Aspirant Inspecteur de Police',
    'Inspecteur de Police',
    'Premier Inspecteur de Police',
    'Aspirant Inspecteur Principal de Police',
    'Inspecteur Principal de Police',
    'Premier Inspecteur Principal de Police',
    'Aspirant Commissaire de Police',
    'Commissaire de Police',
    'Premier Commissaire de Police',
    'Commissaire Divisionnaire de Police',
    'Premier Commissaire Divisionnaire de Police'
  ];

  // États pour la reprise d'encodage
  const [openRepriseDialog, setOpenRepriseDialog] = useState(false);
  const [repriseNumber, setRepriseNumber] = useState('');
  const [repriseYear, setRepriseYear] = useState('');
  const [repriseErrors, setRepriseErrors] = useState({});
  const [repriseLoading, setRepriseLoading] = useState(false);
  const [repriseError, setRepriseError] = useState('');
  const [repriseOptions, setRepriseOptions] = useState({
    redaction: true,
    service: true,
    intervenants: true,
    personnesPhysiques: true,
    personnesMorales: true,
    objets: true,
    vehicules: true,
    rubriques: true
  });

  // États pour le dialog de modification du type de PV
  const [openTypeDialog, setOpenTypeDialog] = useState(false);
  const [reportType, setReportType] = useState('subsequent');
  const [indice, setIndice] = useState('');
  const [indiceError, setIndiceError] = useState('');

  // États pour gérer l'ouverture/fermeture des sections
  const [sectionExpanded, setSectionExpanded] = useState({
    intervenants: true,
    faits: true,
    personnesPhysiques: true,
    personnesMorales: true,
    vehicules: true,
    objets: true
  });

  // Fonction pour toggler l'ouverture/fermeture d'une section
  const toggleSection = (section) => {
    setSectionExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleOpenImportISLP = () => {
    setOpenImportISLP(true);
  };

  const handleCloseImportISLP = () => {
    setOpenImportISLP(false);
  };

  // Fonction pour convertir les acronymes de grade en forme complète
  const convertRankAcronym = (acronym) => {
    if (!acronym) return 'Membre';
    
    const rankMapping = {
      'AAGP': 'Aspirant Agent de Police',
      'AGP': 'Agent de Police',
      '1AGP': 'Premier Agent de Police',
      'ADAGP': 'Aspirant Agent de sécurisation de Police',
      'DAGP': 'Agent de sécurisation de Police',
      '1DAGP': 'Premier Agent de sécurisation de Police',
      'ABASP': 'Aspirant Assistant de Sécurisation de Police',
      'BASP': 'Assistant de Sécurisation de Police',
      '1BASP': 'Premier Assistant de Sécurisation de Police',
      'BCSP': 'Coordonnateur de Sécurisation de Police',
      '1BCSP': 'Premier Coordonnateur de Sécurisation de Police',
      'AINP': 'Aspirant Inspecteur de Police',
      'INP': 'Inspecteur de Police',
      '1INP': 'Premier Inspecteur de Police',
      'AINPP': 'Aspirant Inspecteur Principal de Police',
      'INPP': 'Inspecteur Principal de Police',
      '1INPP': 'Premier Inspecteur Principal de Police',
      'ACP': 'Aspirant Commissaire de Police',
      'CP': 'Commissaire de Police',
      '1CP': 'Premier Commissaire de Police',
      'CDP': 'Commissaire Divisionnaire de Police',
      '1CDP': 'Premier Commissaire Divisionnaire de Police'
    };
    
    return rankMapping[acronym] || acronym;
  };

  // Liste des qualités de personnes concernées (avec possibilité d'ajout dynamique)
  const [personneQualitesList, setPersonneQualitesList] = useState([
    'Suspect',
    'Victime',
    'Témoin',
    'Source',
    'Propriétaire',
    'Contrevenant',
    'Conducteur',
    'Passager',
    'Civilement responsable',
    'Autre'
  ]);

  // Charger les données du rapport
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        if (!report) {
          setLoading(false);
          return;
        }

        const token = sessionStorage.getItem('authToken');
        
        const response = await axios.get(
          `/api/report/search?number=${report.number}&year=${report.year}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setReportData(response.data);
        setUnit(response.data.unit || '');
        setReportType(response.data.reportType || 'subsequent');
        setIndice(response.data.indice || '');
        
        // Initialiser les données de rédaction
        setStartDate(response.data.StartDate || '');
        setEndDate(response.data.EndDate || '');
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement du PV:', error);
        setLoading(false);
      }
    };

    fetchReportData();
  }, [report]);

  // Charger la liste des utilisateurs pour les intervenants
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
      }
    };

    fetchUsers();
  }, []);


  // Écouter les mises à jour via WebSocket
  useEffect(() => {
    if (!socket || !report) return;

    const handleReportUpdated = (updatedReport) => {
      if (updatedReport.number === report.number && 
          updatedReport.year === report.year) {
        
        // Vérifier si cette mise à jour correspond à une action récente (moins de 2 secondes)
        const now = Date.now();
        const isRecentAction = lastActionRef.current.timestamp && 
          (now - lastActionRef.current.timestamp < 2000) && 
          lastActionRef.current.type === Object.keys(updatedReport).find(key => 
            ['unit', 'intervenants', 'faits', 'personnesPhysiques', 'personnesMorales', 'objets', 'vehicules', 'redaction'].includes(key)
          );
          
        if (!isRecentAction) {
          setReportData(prevData => ({
            ...prevData,
            ...updatedReport,
          }));
          
          if (updatedReport.unit) {
            setUnit(updatedReport.unit);
          }
          
          if (updatedReport.reportType !== undefined) {
            setReportType(updatedReport.reportType || 'subsequent');
          }
          
          if (updatedReport.indice !== undefined) {
            setIndice(updatedReport.indice || '');
          }
          
          if (updatedReport.StartDate !== undefined || updatedReport.EndDate !== undefined) {
            setStartDate(updatedReport.StartDate || '');
            setEndDate(updatedReport.EndDate || '');
          }
        }
      }
    };

    socket.on('reportUpdated', handleReportUpdated);

    return () => {
      socket.off('reportUpdated', handleReportUpdated);
    };
  }, [socket, report]);

  // Gestion de l'unité
  const handleOpenUnitDialog = () => {
    setOpenUnitDialog(true);
    setUnitError('');
  };

  const handleCloseUnitDialog = () => {
    setOpenUnitDialog(false);
  };

  const validateUnit = (value) => {
    if (!value || value.trim() === '') {
      setUnitError('L\'unité est obligatoire');
      return false;
    }
    setUnitError('');
    return true;
  };

  const handleSaveUnit = async () => {
    if (!validateUnit(unit)) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'unit',
        id: null,
        timestamp: Date.now()
      };

      // Mise à jour locale immédiate
      setReportData(prevData => ({
        ...prevData,
        unit
      }));
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          unit: unit,
          number: report.number,
          year: report.year
        });
      }

      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/unit`,
        { unit: unit },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      // Log de la modification du service
      logAction(LOG_ACTIONS.ELEMENTS_UNIT_UPDATE, formatReportNumber(report.number, report.year), {
        ancienService: reportData?.unit || null,
        nouveauService: unit
      });
      
      handleCloseUnitDialog();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'unité:', error);
      handleCloseUnitDialog();
    }
  };

  // Fonction pour convertir une date ISO en format datetime-local
  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error);
      return '';
    }
  };

  // Fonction pour convertir une date ISO en format date (YYYY-MM-DD)
  const formatDateLocal = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error);
      return '';
    }
  };

  // Gestion de la rédaction
  const handleOpenRedactionDialog = () => {
    setOpenRedactionDialog(true);
    setStartDate(formatDateTimeLocal(reportData?.StartDate) || '');
    setEndDate(formatDateLocal(reportData?.EndDate) || '');
    setRedactionErrors({});
  };

  const handleCloseRedactionDialog = () => {
    setOpenRedactionDialog(false);
  };

  const validateRedactionForm = () => {
    const errors = {};
    setRedactionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveRedaction = async () => {
    if (!validateRedactionForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'redaction',
        id: null,
        timestamp: Date.now()
      };

      // Mise à jour locale immédiate
      setReportData(prevData => ({
        ...prevData,
        StartDate: startDate,
        EndDate: endDate
      }));
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          StartDate: startDate,
          EndDate: endDate,
          number: report.number,
          year: report.year
        });
      }

      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/redaction`,
        { 
          StartDate: startDate,
          EndDate: endDate
        },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      // Log de la modification des dates de rédaction
      logAction(LOG_ACTIONS.ELEMENTS_REDACTION_UPDATE, formatReportNumber(report.number, report.year), {
        ancienneStartDate: reportData?.StartDate || null,
        ancienneEndDate: reportData?.EndDate || null,
        nouvelleStartDate: startDate,
        nouvelleEndDate: endDate
      });
      
      handleCloseRedactionDialog();
    } catch (error) {
      console.error('Erreur lors de la mise à jour des informations de rédaction:', error);
      handleCloseRedactionDialog();
    }
  };

  // Fonction pour mettre à jour seulement la date de clôture à aujourd'hui
  const handleSetEndDateToday = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      // Utiliser l'heure locale au lieu de UTC pour obtenir la date du jour correcte
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const today = `${year}-${month}-${day}`;

      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'redaction',
        id: null,
        timestamp: Date.now()
      };

      // Mise à jour locale immédiate
      setReportData(prevData => ({
        ...prevData,
        EndDate: today
      }));
      
      setEndDate(today);
      
      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          StartDate: reportData?.StartDate,
          EndDate: today,
          number: report.number,
          year: report.year
        });
      }

      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/redaction`,
        { 
          StartDate: reportData?.StartDate,
          EndDate: today
        },
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      // Log de la modification de la date de clôture
      logAction(LOG_ACTIONS.ELEMENTS_REDACTION_UPDATE, formatReportNumber(report.number, report.year), {
        ancienneEndDate: reportData?.EndDate || null,
        nouvelleEndDate: today,
        action: 'date_cloture_aujourdhui'
      });
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la date de clôture:', error);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteDialog = (type, itemId) => {
    setDeleteType(type);
    setItemToDelete(itemId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setItemToDelete(null);
    setDeleteType('');
  };

  const handleDeleteItem = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId || !itemToDelete) {
        console.error('ID du rapport ou de l\'élément non disponible');
        handleCloseDeleteDialog();
        return;
      }

      // Récupérer les données de l'élément avant suppression pour le log
      const deletedItemData = reportData?.[deleteType]?.find(item => item._id === itemToDelete);

      // Vérifier si l'ID est temporaire
      if (itemToDelete.startsWith && itemToDelete.startsWith('temp-')) {
        // Pour les IDs temporaires, on fait juste une suppression locale
        setReportData(prevData => {
          const updatedItems = prevData[deleteType].filter(item => item._id !== itemToDelete);
          return {
            ...prevData,
            [deleteType]: updatedItems
          };
        });
        
        handleCloseDeleteDialog();
        return;
      }

      const endpoints = {
        intervenants: '/intervenants',
        faits: '/faits',
        personnesPhysiques: '/personnes-physiques',
        personnesMorales: '/personnes-morales',
        objets: '/objets',
        vehicules: '/vehicules'
      };

      const endpoint = endpoints[deleteType];
      const url = `/api/report/${reportId}${endpoint}/${itemToDelete}`;
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: deleteType,
        id: itemToDelete,
        timestamp: Date.now()
      };
      
      // Suppression locale immédiate
      setReportData(prevData => {
        const updatedItems = prevData[deleteType].filter(item => item._id !== itemToDelete);
        return {
          ...prevData,
          [deleteType]: updatedItems
        };
      });
      
      // Suppression via API
      await axios.delete(url, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      // Log de la suppression avec les données de l'élément
      const deleteLogActions = {
        intervenants: LOG_ACTIONS.ELEMENTS_INTERVENANT_DELETE,
        faits: LOG_ACTIONS.ELEMENTS_FAIT_DELETE,
        personnesPhysiques: LOG_ACTIONS.ELEMENTS_PERSONNE_DELETE,
        personnesMorales: LOG_ACTIONS.ELEMENTS_MORALE_DELETE,
        objets: LOG_ACTIONS.ELEMENTS_OBJET_DELETE,
        vehicules: LOG_ACTIONS.ELEMENTS_VEHICULE_DELETE
      };
      
      logAction(deleteLogActions[deleteType], formatReportNumber(report.number, report.year), {
        type: deleteType,
        elementId: itemToDelete,
        elementData: deletedItemData || null
      });
      
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'élément:', error);
      handleCloseDeleteDialog();
    }
  };

  const handleCloseNotification = () => {
    setCopyNotification(false);
  };

  const showCopyNotification = (text) => {
    setCopiedText(text);
    setCopyNotification(true);
  };

  const handleCopyToClipboard = (text, html = null) => {
    const copyWithExecCommand = (content, isHtml = false) => {
      if (typeof document === 'undefined' || typeof window === 'undefined') {
        return false;
      }
      
      const element = isHtml ? document.createElement('div') : document.createElement('textarea');
      element.style.position = 'fixed';
      element.style.left = '-9999px';
      element.style.top = '0';
      element.style.pointerEvents = 'none';
      element.style.opacity = '0';
      element.setAttribute('aria-hidden', 'true');
      
      if (isHtml) {
        element.setAttribute('contenteditable', 'true');
        element.innerHTML = content;
      } else {
        element.value = content;
      }
      
      document.body.appendChild(element);
      
      let selection = null;
      if (isHtml) {
        const range = document.createRange();
        range.selectNodeContents(element);
        selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        element.focus();
        element.select();
      }
      
      let success = false;
      try {
        success = document.execCommand('copy');
      } catch (error) {
        console.error('Erreur lors de la copie via execCommand:', error);
      }
      
      if (selection) {
        selection.removeAllRanges();
      }
      document.body.removeChild(element);
      return success;
    };
    
    const copyTextFallback = () => {
      const success = copyWithExecCommand(text, false);
      if (success) {
        showCopyNotification(text);
      }
      return success;
    };
    
    if (html) {
      const htmlCopied = copyWithExecCommand(html, true);
      if (htmlCopied) {
        showCopyNotification(text);
        return;
      }
    }
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          showCopyNotification(text);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          copyTextFallback();
        });
    } else {
      copyTextFallback();
    }
  };

  // Fonctions pour gérer les intervenants
  const resetIntervenantForm = () => {
    setIsExternalMember(false);
    setSelectedUserId('');
    setIsSignataire(false);
    setIsOPJ(false);
    setIsOPA(false);
    setRank('');
    setLastname('');
    setFirstname('');
    setIntervenantErrors({});
  };

  const syncFlagsFromUser = (userId, fallback = { opj: false, opa: false }) => {
    if (!userId) {
      setIsOPJ(!!fallback.opj);
      setIsOPA(!!fallback.opa);
      return false;
    }
    const selectedUser = users.find(u => u._id === userId);
    if (selectedUser) {
      setIsOPJ(!!selectedUser.opj);
      setIsOPA(!!selectedUser.opa);
      return true;
    }
    setIsOPJ(!!fallback.opj);
    setIsOPA(!!fallback.opa);
    return false;
  };

  const handleOpenIntervenantDialog = (mode = 'add', itemId = null) => {
    setIntervenantMode(mode);
    setIntervenantItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const intervenant = reportData.intervenants.find(item => item._id === itemId);
      if (intervenant) {
        const isExternal = intervenant.isExternal;
        setIsExternalMember(isExternal);
        setIsSignataire(intervenant.sign || false);
        setRank(intervenant.rank);
        setLastname(intervenant.lastname);
        setFirstname(intervenant.firstname);
        setSelectedUserId(intervenant.userId || '');
        if (isExternal) {
          setIsOPJ(!!intervenant.isOPJ);
          setIsOPA(!!intervenant.isOPA);
        } else if (!syncFlagsFromUser(intervenant.userId, { opj: intervenant.isOPJ, opa: intervenant.isOPA })) {
          setIsOPJ(!!intervenant.isOPJ);
          setIsOPA(!!intervenant.isOPA);
        }
      }
    } else {
      resetIntervenantForm();
    }
    
    setOpenIntervenantDialog(true);
  };

  const handleCloseIntervenantDialog = () => {
    setOpenIntervenantDialog(false);
    resetIntervenantForm();
  };

  const validateIntervenantForm = () => {
    const errors = {};
    
    if (isExternalMember) {
      if (!rank) errors.rank = 'Le grade est obligatoire';
      if (!lastname) errors.lastname = 'Le nom est obligatoire';
      if (!firstname) errors.firstname = 'Le prénom est obligatoire';
    } else {
      if (!selectedUserId) errors.userId = 'Veuillez sélectionner un utilisateur';
    }
    
    setIntervenantErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveIntervenant = async () => {
    if (!validateIntervenantForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      let intervenantData;
      
      if (isExternalMember) {
        intervenantData = {
          isExternal: true,
          sign: isSignataire,
          isOPJ: !!isOPJ,
          isOPA: !!isOPA,
          rank,
          lastname,
          firstname
        };
      } else {
        const selectedUser = users.find(u => u._id === selectedUserId);
        if (!selectedUser) {
          setIntervenantErrors({ userId: 'Utilisateur non trouvé' });
          return;
        }

        intervenantData = {
          isExternal: false,
          sign: isSignataire,
          isOPJ: !!selectedUser.opj,
          isOPA: !!selectedUser.opa,
          rank: convertRankAcronym(selectedUser.rank) || 'Membre',
          lastname: selectedUser.lastname,
          firstname: selectedUser.firstname,
          userId: selectedUser._id
        };
      }

      const endpoint = `/intervenants${intervenantMode === 'edit' ? `/${intervenantItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = intervenantMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'intervenants',
        id: intervenantItemId,
        timestamp: Date.now()
      };
      
      if (intervenantMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: intervenantData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.intervenants];
          const index = updatedItems.findIndex(item => item._id === intervenantItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...intervenantData
            };
          }
          return {
            ...prevData,
            intervenants: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: intervenantData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification d'intervenant
      const logActionType = intervenantMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_INTERVENANT_ADD 
        : LOG_ACTIONS.ELEMENTS_INTERVENANT_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: intervenantMode,
        intervenant: intervenantData
      });
      
      handleCloseIntervenantDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${intervenantMode === 'add' ? 'ajout' : 'édition'} de l'intervenant:`, error);
    }
  };

  const handleCopyIntervenant = (intervenant) => {
    const rankLabel = convertRankAcronym(intervenant.rank);
    const namePart = `${intervenant.lastname?.toUpperCase() || ''}${intervenant.firstname ? ` ${intervenant.firstname}` : ''}`.trim();
    const textToCopy = `${rankLabel ? `${rankLabel} ` : ''}${namePart}`.trim();
    const htmlContent = `${rankLabel ? `${rankLabel} ` : ''}<strong>${namePart}</strong>`;

    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'intervenant',
        contenu: textToCopy
      });
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(textToCopy);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          handleCopyToClipboard(textToCopy, htmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, htmlContent);
      logCopy();
    }
  };

  // Fonctions pour gérer les personnes concernées
  const resetPersonneForm = () => {
    setPersonneQualite('');
    setAdditionalQualities([]);
    setIsUnknown(false);
    setPersonneLastname('');
    setPersonneFirstname('');
    setOtherFirstnames('');
    setGender('');
    setBirthPlace('');
    setBirthdate('');
    setCivilStatus('');
    setNationality('');
    setProfession('');
    setResidence('');
    setNationalNumber('');
    setRaavisNumber('');
    setPhotographies('');
    setEmpreintes('');
    setNoticeIndividuelle('');
    setPersonneErrors({});
  };

  const handleOpenPersonneDialog = (mode = 'add', itemId = null) => {
    setPersonneMode(mode);
    setPersonneItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const personne = reportData.personnesPhysiques.find(item => item._id === itemId);
      if (personne) {
        setPersonneQualite(personne.qualite);
        setAdditionalQualities(personne.additionalQualities || []);
        setIsUnknown(personne.isUnknown);
        setPersonneLastname(personne.lastname || '');
        setPersonneFirstname(personne.firstname || '');
        setOtherFirstnames(personne.otherFirstnames || '');
        setGender(personne.gender || '');
        setBirthPlace(personne.birthPlace || '');
        setBirthdate(personne.birthdate || '');
        setCivilStatus(personne.civilStatus || '');
        setNationality(personne.nationality || '');
        setProfession(personne.profession || '');
        setResidence(personne.residence || '');
        setNationalNumber(personne.nationalNumber || '');
        setRaavisNumber((personne.raavisNumber || '').toUpperCase());
        setPhotographies(personne.photographies || '');
        setEmpreintes(personne.empreintes || '');
        setNoticeIndividuelle(personne.noticeIndividuelle || '');
      }
    } else {
      resetPersonneForm();
    }
    
    setOpenPersonneDialog(true);
  };

  const handleClosePersonneDialog = () => {
    setOpenPersonneDialog(false);
    resetPersonneForm();
  };

  const validatePersonneForm = () => {
    const errors = {};
    
    if (!personneQualite) {
      errors.qualite = 'La qualité est obligatoire';
    }
    
    if (!isUnknown && !personneLastname) {
      errors.lastname = 'Le nom est obligatoire pour une personne identifiée';
    }
    
    setPersonneErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePersonne = async () => {
    if (!validatePersonneForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      const normalizedRaavisNumber = (raavisNumber || '').toUpperCase();

      const personneData = {
        qualite: personneQualite,
        additionalQualities: additionalQualities,
        isUnknown,
        lastname: isUnknown ? null : personneLastname,
        firstname: isUnknown ? null : personneFirstname,
        otherFirstnames: isUnknown ? null : otherFirstnames,
        gender: isUnknown ? null : gender,
        birthPlace: isUnknown ? null : birthPlace,
        birthdate: isUnknown ? null : birthdate,
        civilStatus: isUnknown ? null : civilStatus,
        nationality: isUnknown ? null : nationality,
        profession: isUnknown ? null : profession,
        residence: isUnknown ? null : residence,
        nationalNumber: isUnknown ? null : nationalNumber,
        raavisNumber: isUnknown ? null : normalizedRaavisNumber,
        photographies: isUnknown ? '' : photographies,
        empreintes: isUnknown ? '' : empreintes,
        noticeIndividuelle: isUnknown ? '' : noticeIndividuelle
      };

      const endpoint = `/personnes-physiques${personneMode === 'edit' ? `/${personneItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = personneMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'personnesPhysiques',
        id: personneItemId,
        timestamp: Date.now()
      };
      
      if (personneMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: personneData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.personnesPhysiques];
          const index = updatedItems.findIndex(item => item._id === personneItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...personneData
            };
          }
          return {
            ...prevData,
            personnesPhysiques: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: personneData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification de personne physique
      const logActionType = personneMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_PERSONNE_ADD 
        : LOG_ACTIONS.ELEMENTS_PERSONNE_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: personneMode,
        personne: personneData
      });
      
      handleClosePersonneDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${personneMode === 'add' ? 'ajout' : 'édition'} de la personne:`, error);
    }
  };

  const handleCopyPersonneNom = (personne) => {
    if (personne.isUnknown) return;
    
    const lastname = personne.lastname?.toUpperCase() || '';
    const firstname = personne.firstname || '';
    const textToCopy = `${lastname} ${firstname}`.trim();
    
    const parts = [];
    if (lastname) {
      parts.push(`<strong><u>${escapeHtml(lastname)}</u></strong>`);
    }
    if (firstname) {
      const separator = parts.length > 0 ? ' ' : '';
      parts.push(`${separator}<strong>${escapeHtml(firstname)}</strong>`);
    }
    const htmlContent = parts.join('');
    
    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'personne_nom',
        contenu: textToCopy
      });
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(textToCopy);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          handleCopyToClipboard(textToCopy, htmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, htmlContent);
      logCopy();
    }
  };

  const handleCopyPersonneInfo = (personne) => {
    if (personne.isUnknown) return;
    
    const genderLabel = personne.gender === 'M' ? 'Masculin' : personne.gender === 'F' ? 'Féminin' : '';
    const birthdateLabel = personne.birthdate ? formatBirthdate(personne.birthdate) : '';
    const leftCellSections = [];
    
    if (personne.qualite) {
      leftCellSections.push(
        `<div style="margin-bottom:4px;"><strong>${escapeHtml(personne.qualite)}</strong></div>`
      );
    }
    
    if (personne.additionalQualities && personne.additionalQualities.length > 0) {
      leftCellSections.push(
        `<div style="margin-top:4px;"><em>${escapeHtml(personne.additionalQualities.join(', '))}</em></div>`
      );
    }
    
    const nameParts = [];
    if (personne.lastname) {
      nameParts.push(`<strong><u>${escapeHtml(personne.lastname.toUpperCase())}</u></strong>`);
    }
    if (personne.firstname) {
      nameParts.push(` <strong>${escapeHtml(personne.firstname)}</strong>`);
    }
    if (personne.otherFirstnames) {
      nameParts.push(` ${escapeHtml(personne.otherFirstnames)}`);
    }
    
    const rightCellSections = [];
    
    if (nameParts.length > 0) {
      rightCellSections.push(`<div style="margin-bottom:6px;">${nameParts.join('').trim()}</div>`);
    }
    
    if (genderLabel) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">Sexe : ${genderLabel}</div>`
      );
    }
    
    if (birthdateLabel) {
      let birthLine = `Né le ${birthdateLabel}`;
      if (personne.birthPlace) {
        birthLine += ` à ${escapeHtml(personne.birthPlace)}`;
      }
      rightCellSections.push(`<div style="margin-bottom:4px;">${birthLine}</div>`);
    }
    
    if (personne.civilStatus) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">État civil : ${escapeHtml(personne.civilStatus)}</div>`
      );
    }
    
    if (personne.nationality) {
      let natLine = `Nationalité : ${escapeHtml(personne.nationality)}`;
      if (personne.nationalNumber) {
        natLine += ` (RRN : ${escapeHtml(personne.nationalNumber)})`;
      }
      rightCellSections.push(`<div style="margin-bottom:4px;">${natLine}</div>`);
    } else if (personne.nationalNumber) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">RRN : ${escapeHtml(personne.nationalNumber)}</div>`
      );
    }
    
    if (personne.profession) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">Profession : ${escapeHtml(personne.profession)}</div>`
      );
    }
    
    if (personne.residence) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">Domicile : ${escapeHtml(personne.residence)}</div>`
      );
    }
    
    if (personne.raavisNumber) {
      const statusParts = [];
      const formatStatusSymbol = (label, value) => {
        if (!value || value.toLowerCase() === 'ne pas mentionner') return null;
        const normalized = value.toLowerCase();
        if (normalized === 'oui') return `${label} <strong>&#10003;</strong>`;
        if (normalized === 'non') return `${label} <strong>&#10007;</strong>`;
        return null;
      };
      
      const empreintes = formatStatusSymbol('Empreintes', personne.empreintes || '');
      const photographies = formatStatusSymbol('Photographies', personne.photographies || '');
      const notice = formatStatusSymbol('Notice individuelle', personne.noticeIndividuelle || '');
      
      [empreintes, photographies, notice].forEach(status => {
        if (status) statusParts.push(status);
      });
      
      const suffix = statusParts.length ? ` (${statusParts.join(' - ')})` : '';
      
      rightCellSections.push(
        `<div style="margin-bottom:4px;">N° APFIS : <strong>${escapeHtml(personne.raavisNumber)}</strong>${suffix}</div>`
      );
    }
    
    const leftCellContent = leftCellSections.length
      ? `<div style="display:flex; flex-direction:column; justify-content:flex-start; align-items:flex-start; text-align:left; gap:4px; width:100%;">${leftCellSections.join('')}</div>`
      : '&nbsp;';
    
    const tableHtml = `
      <table style="width:100%; border-collapse:separate; border-spacing:0; background-color:transparent; table-layout:fixed;">
        <colgroup>
          <col style="width:130px; max-width:130px; min-width:130px;" />
          <col style="width:auto;" />
        </colgroup>
        <tbody>
          <tr>
            <td width="130" valign="top" style="width:130px; min-width:130px; max-width:130px; vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${leftCellContent}
            </td>
            <td valign="top" style="vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${rightCellSections.length ? rightCellSections.join('') : '&nbsp;'}
            </td>
          </tr>
        </tbody>
      </table>
    `;
    
    const tableHtmlContent = tableHtml.trim();

    const textSections = [];
    if (personne.qualite) {
      textSections.push(`Qualité\n${personne.qualite}`);
    }
    if (personne.additionalQualities && personne.additionalQualities.length > 0) {
      textSections.push(`Qualités additionnelles\n${personne.additionalQualities.join(', ')}`);
    }
    if (nameParts.length > 0) {
      textSections.push(
        `${personne.lastname?.toUpperCase() || ''} ${personne.firstname || ''}${personne.otherFirstnames ? ` (${personne.otherFirstnames})` : ''}`.trim()
      );
    }
    if (genderLabel) {
      textSections.push(`Sexe : ${genderLabel}`);
    }
    if (birthdateLabel) {
      let birthText = `Né le ${birthdateLabel}`;
      if (personne.birthPlace) {
        birthText += ` à ${personne.birthPlace}`;
      }
      textSections.push(birthText);
    }
    if (personne.civilStatus) {
      textSections.push(`État civil : ${personne.civilStatus}`);
    }
    if (personne.nationality) {
      let natText = `Nationalité : ${personne.nationality}`;
      if (personne.nationalNumber) {
        natText += ` (RRN : ${personne.nationalNumber})`;
      }
      textSections.push(natText);
    } else if (personne.nationalNumber) {
      textSections.push(`RRN : ${personne.nationalNumber}`);
    }
    if (personne.profession) {
      textSections.push(`Profession : ${personne.profession}`);
    }
    if (personne.residence) {
      textSections.push(`Domicile : ${personne.residence}`);
    }
    if (personne.raavisNumber) {
      const statusTextParts = [];
      const toSymbol = (value) => {
        if (!value || value.toLowerCase() === 'ne pas mentionner') return null;
        const normalized = value.toLowerCase();
        if (normalized === 'oui') return '✓';
        if (normalized === 'non') return '✗';
        return null;
      };
      const empreintesSymbol = toSymbol(personne.empreintes || '');
      const photosSymbol = toSymbol(personne.photographies || '');
      const noticeSymbol = toSymbol(personne.noticeIndividuelle || '');
      
      if (empreintesSymbol) statusTextParts.push(`Empreintes ${empreintesSymbol}`);
      if (photosSymbol) statusTextParts.push(`Photographies ${photosSymbol}`);
      if (noticeSymbol) statusTextParts.push(`Notice individuelle ${noticeSymbol}`);
      
      const statusText = statusTextParts.length ? ` (${statusTextParts.join(' - ')})` : '';
      textSections.push(`N° APFIS : ${personne.raavisNumber}${statusText}`);
    }
    
    const textToCopy = textSections.join('\n');
    
    const notificationLabel = `${personne.lastname?.toUpperCase() || ''} ${personne.firstname || ''}`.trim() || 'Informations copiées';
    
    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'personne_info',
        contenu: textToCopy?.substring(0, 500)
      });
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([tableHtmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' }),
      });
      
      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(notificationLabel);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          handleCopyToClipboard(textToCopy, tableHtmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, tableHtmlContent);
      logCopy();
    }
  };

  // Fonctions pour gérer les objets
  const resetObjetForm = () => {
    setDescription('');
    setSinNumber('');
    setObjetErrors({});
  };

  const handleOpenObjetDialog = (mode = 'add', itemId = null) => {
    setObjetMode(mode);
    setObjetItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const objet = reportData.objets.find(item => item._id === itemId);
      if (objet) {
        setDescription(objet.description);
        setSinNumber(objet.sinNumber || '');
      }
    } else {
      resetObjetForm();
    }
    
    setOpenObjetDialog(true);
  };

  const handleCloseObjetDialog = () => {
    setOpenObjetDialog(false);
    resetObjetForm();
  };

  const validateObjetForm = () => {
    const errors = {};
    
    if (!description) {
      errors.description = 'La description est obligatoire';
    }
    
    setObjetErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveObjet = async () => {
    if (!validateObjetForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      const objetData = {
        description,
        sinNumber
      };

      const endpoint = `/objets${objetMode === 'edit' ? `/${objetItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = objetMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'objets',
        id: objetItemId,
        timestamp: Date.now()
      };
      
      if (objetMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: objetData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.objets];
          const index = updatedItems.findIndex(item => item._id === objetItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...objetData
            };
          }
          return {
            ...prevData,
            objets: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: objetData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification d'objet
      const logActionType = objetMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_OBJET_ADD 
        : LOG_ACTIONS.ELEMENTS_OBJET_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: objetMode,
        objet: objetData
      });
      
      handleCloseObjetDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${objetMode === 'add' ? 'ajout' : 'édition'} de l'objet:`, error);
    }
  };

  const handleCopyObjet = (objet) => {
    if (!objet?.description) return;

    let textToCopy = objet.description;
    if (objet.sinNumber) {
      textToCopy += ` (SIN ${objet.sinNumber})`;
    }

    const htmlContent = objet.sinNumber
      ? `${escapeHtml(objet.description)} (<strong>SIN ${escapeHtml(objet.sinNumber)}</strong>)`
      : null;

    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'objet',
        contenu: textToCopy
      });
    };

    if (objet.sinNumber && navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem])
        .then(() => { logCopy(); showCopyNotification(textToCopy); })
        .catch(() => { handleCopyToClipboard(textToCopy, htmlContent); logCopy(); });
    } else {
      handleCopyToClipboard(textToCopy, htmlContent);
      logCopy();
    }
  };

  // Fonctions pour gérer les véhicules
  const resetVehiculeForm = () => {
    setBrand('');
    setVehiculeCategory('');
    setPlateNumber('');
    setChassisNumber('');
    setVehiculeNationality('');
    setVehiculeColor('');
    setInsurance('');
    setTechnicalControl('');
    setVehiculeErrors({});
  };

  const handleOpenVehiculeDialog = (mode = 'add', itemId = null) => {
    setVehiculeMode(mode);
    setVehiculeItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const vehicule = reportData.vehicules.find(item => item._id === itemId);
      if (vehicule) {
        setBrand(vehicule.brand || '');
        setVehiculeCategory(vehicule.category || '');
        setPlateNumber((vehicule.plateNumber || '').toUpperCase());
        setChassisNumber((vehicule.chassisNumber || '').toUpperCase());
        setVehiculeNationality(vehicule.nationality || '');
        setVehiculeColor(vehicule.color || '');
        setInsurance(vehicule.insurance || '');
        setTechnicalControl(vehicule.technicalControl || '');
      }
    } else {
      resetVehiculeForm();
    }
    
    setOpenVehiculeDialog(true);
  };

  const handleCloseVehiculeDialog = () => {
    setOpenVehiculeDialog(false);
    resetVehiculeForm();
  };

  const validateVehiculeForm = () => {
    const errors = {};
    
    if (!brand) {
      errors.brand = 'La marque/modèle est obligatoire';
    }
    
    setVehiculeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveVehicule = async () => {
    if (!validateVehiculeForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      const vehiculeData = {
        brand,
        category: vehiculeCategory,
        plateNumber: plateNumber.toUpperCase(),
        chassisNumber: chassisNumber.toUpperCase(),
        nationality: vehiculeNationality,
        color: vehiculeColor,
        insurance,
        technicalControl
      };

      const endpoint = `/vehicules${vehiculeMode === 'edit' ? `/${vehiculeItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = vehiculeMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'vehicules',
        id: vehiculeItemId,
        timestamp: Date.now()
      };
      
      if (vehiculeMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: vehiculeData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.vehicules];
          const index = updatedItems.findIndex(item => item._id === vehiculeItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...vehiculeData
            };
          }
          return {
            ...prevData,
            vehicules: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: vehiculeData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification de véhicule
      const logActionType = vehiculeMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_VEHICULE_ADD 
        : LOG_ACTIONS.ELEMENTS_VEHICULE_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: vehiculeMode,
        vehicule: vehiculeData
      });
      
      handleCloseVehiculeDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${vehiculeMode === 'add' ? 'ajout' : 'édition'} du véhicule:`, error);
    }
  };

  const handleCopyVehiculeSimple = (vehicule) => {
    if (!vehicule?.brand) return;
    
    const baseText = vehicule.brand;
    const plateText = vehicule.plateNumber ? ` immatriculé ${vehicule.plateNumber}` : '';
    const textToCopy = `${baseText}${plateText}`.trim();
    const htmlContent = vehicule.plateNumber
      ? `${escapeHtml(baseText)} immatriculé <strong>${escapeHtml(vehicule.plateNumber)}</strong>`
      : null;

    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'vehicule_simple',
        contenu: textToCopy
      });
    };

    if (vehicule.plateNumber && navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(textToCopy);
        })
        .catch(() => {
          handleCopyToClipboard(textToCopy, htmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, htmlContent);
      logCopy();
    }
  };

  const handleCopyVehiculeInfo = (vehicule) => {
    if (!vehicule) return;

    const rightCellSections = [];
    const textSections = [];

    if (vehicule.brand) {
      rightCellSections.push(`<div style="margin-bottom:4px;"><strong><u>${escapeHtml(vehicule.brand)}</u></strong></div>`);
      textSections.push(vehicule.brand);
    }

    if (vehicule.plateNumber) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Immatriculation : <strong>${escapeHtml(vehicule.plateNumber)}</strong></div>`);
      textSections.push(`Immatriculation : ${vehicule.plateNumber}`);
    }

    if (vehicule.chassisNumber) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Châssis : <strong>${escapeHtml(vehicule.chassisNumber)}</strong></div>`);
      textSections.push(`Châssis : ${vehicule.chassisNumber}`);
    }

    if (vehicule.nationality) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Nationalité : ${escapeHtml(vehicule.nationality)}</div>`);
      textSections.push(`Nationalité : ${vehicule.nationality}`);
    }

    if (vehicule.color) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Teinte : ${escapeHtml(vehicule.color)}</div>`);
      textSections.push(`Teinte : ${vehicule.color}`);
    }

    if (vehicule.insurance) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Assurance : ${escapeHtml(vehicule.insurance)}</div>`);
      textSections.push(`Assurance : ${vehicule.insurance}`);
    }

    if (vehicule.technicalControl) {
      rightCellSections.push(`<div style="margin-bottom:2px;">Contrôle technique : ${escapeHtml(vehicule.technicalControl)}</div>`);
      textSections.push(`Contrôle technique : ${vehicule.technicalControl}`);
    }

    const leftCellContent = vehicule.category
      ? `<div style="display:flex; flex-direction:column; justify-content:flex-start; align-items:flex-start; text-align:left; gap:4px; width:100%;"><strong>${escapeHtml(vehicule.category)}</strong></div>`
      : '&nbsp;';

    const tableHtml = `
      <table style="width:100%; border-collapse:separate; border-spacing:0; background-color:transparent; table-layout:fixed;">
        <colgroup>
          <col style="width:130px; max-width:130px; min-width:130px;" />
          <col style="width:auto;" />
        </colgroup>
        <tbody>
          <tr>
            <td width="130" valign="top" style="width:130px; min-width:130px; max-width:130px; vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${leftCellContent}
            </td>
            <td valign="top" style="vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${rightCellSections.length ? rightCellSections.join('') : '&nbsp;'}
            </td>
          </tr>
        </tbody>
      </table>
    `.trim();

    if (vehicule.category) {
      textSections.unshift(vehicule.category);
    }

    const textToCopy = textSections.join('\n');
    const notificationLabel = vehicule.brand || vehicule.category || 'Véhicule copié';

    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'vehicule_info',
        contenu: textToCopy?.substring(0, 500)
      });
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([tableHtml], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });

      navigator.clipboard.write([clipboardItem])
        .then(() => { logCopy(); showCopyNotification(notificationLabel); })
        .catch(() => { handleCopyToClipboard(textToCopy, tableHtml); logCopy(); });
    } else {
      handleCopyToClipboard(textToCopy, tableHtml);
      logCopy();
    }
  };

  // Fonctions pour gérer les personnes morales
  const resetPersonneMoraleForm = () => {
    setPersonneMoraleQualite('');
    setDenomination('');
    setCompanyNumber('');
    setHeadquarters('');
    setPersonneMoraleErrors({});
  };

  const handleOpenPersonneMoraleDialog = (mode = 'add', itemId = null) => {
    setPersonneMoraleMode(mode);
    setPersonneMoraleItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const personneMorale = reportData.personnesMorales.find(item => item._id === itemId);
      if (personneMorale) {
        setPersonneMoraleQualite(personneMorale.qualite || '');
        setDenomination(personneMorale.denomination);
        setCompanyNumber(personneMorale.companyNumber || '');
        setHeadquarters(personneMorale.headquarters || '');
      }
    } else {
      resetPersonneMoraleForm();
    }
    
    setOpenPersonneMoraleDialog(true);
  };

  const handleClosePersonneMoraleDialog = () => {
    setOpenPersonneMoraleDialog(false);
    resetPersonneMoraleForm();
  };

  const validatePersonneMoraleForm = () => {
    const errors = {};
    
    if (!personneMoraleQualite) {
      errors.qualite = 'La qualité est obligatoire';
    }
    
    if (!denomination) {
      errors.denomination = 'La dénomination est obligatoire';
    }
    
    setPersonneMoraleErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePersonneMorale = async () => {
    if (!validatePersonneMoraleForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      const personneMoraleData = {
        qualite: personneMoraleQualite,
        denomination,
        companyNumber,
        headquarters
      };

      const endpoint = `/personnes-morales${personneMoraleMode === 'edit' ? `/${personneMoraleItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = personneMoraleMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'personnesMorales',
        id: personneMoraleItemId,
        timestamp: Date.now()
      };
      
      if (personneMoraleMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: personneMoraleData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.personnesMorales];
          const index = updatedItems.findIndex(item => item._id === personneMoraleItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...personneMoraleData
            };
          }
          return {
            ...prevData,
            personnesMorales: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: personneMoraleData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification de personne morale
      const logActionType = personneMoraleMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_MORALE_ADD 
        : LOG_ACTIONS.ELEMENTS_MORALE_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: personneMoraleMode,
        personneMorale: personneMoraleData
      });
      
      handleClosePersonneMoraleDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${personneMoraleMode === 'add' ? 'ajout' : 'édition'} de la personne morale:`, error);
    }
  };

  const handleCopyPersonneMoraleNom = (personneMorale) => {
    if (!personneMorale?.denomination) return;
    
    const textToCopy = personneMorale.denomination;
    const htmlContent = `<strong><u>${escapeHtml(textToCopy)}</u></strong>`;
    
    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'personne_morale_nom',
        contenu: textToCopy
      });
    };
    
    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
      });
      
      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(textToCopy);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          handleCopyToClipboard(textToCopy, htmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, htmlContent);
      logCopy();
    }
  };

  const handleCopyPersonneMoraleInfo = (personneMorale) => {
    if (!personneMorale) return;
    
    const leftCellSections = [];
    if (personneMorale.qualite) {
      leftCellSections.push(
        `<div style="margin-bottom:4px;"><strong>${escapeHtml(personneMorale.qualite)}</strong></div>`
      );
    }
    
    const rightCellSections = [];
    if (personneMorale.denomination) {
      rightCellSections.push(
        `<div style="margin-bottom:6px;"><strong><u>${escapeHtml(personneMorale.denomination)}</u></strong></div>`
      );
    }
    
    if (personneMorale.companyNumber) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">Numéro d'entreprise : ${escapeHtml(personneMorale.companyNumber)}</div>`
      );
    }
    
    if (personneMorale.headquarters) {
      rightCellSections.push(
        `<div style="margin-bottom:4px;">Siège social : ${escapeHtml(personneMorale.headquarters)}</div>`
      );
    }
    
    const leftCellContent = leftCellSections.length
      ? `<div style="display:flex; flex-direction:column; justify-content:flex-start; align-items:flex-start; text-align:left; gap:4px; width:100%;">${leftCellSections.join('')}</div>`
      : '&nbsp;';
    
    const tableHtml = `
      <table style="width:100%; border-collapse:separate; border-spacing:0; background-color:transparent; table-layout:fixed;">
        <colgroup>
          <col style="width:130px; max-width:130px; min-width:130px;" />
          <col style="width:auto;" />
        </colgroup>
        <tbody>
          <tr>
            <td width="130" valign="top" style="width:130px; min-width:130px; max-width:130px; vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${leftCellContent}
            </td>
            <td valign="top" style="vertical-align:top; padding:4px 8px; background-color:transparent; word-break:break-word; overflow-wrap:break-word; white-space:normal;">
              ${rightCellSections.length ? rightCellSections.join('') : '&nbsp;'}
            </td>
          </tr>
        </tbody>
      </table>
    `;
    
    const tableHtmlContent = tableHtml.trim();
    
    const textSections = [];
    if (personneMorale.qualite) {
      textSections.push(`Qualité\n${personneMorale.qualite}`);
    }
    if (personneMorale.denomination) {
      textSections.push(personneMorale.denomination);
    }
    if (personneMorale.companyNumber) {
      textSections.push(`Numéro d'entreprise : ${personneMorale.companyNumber}`);
    }
    if (personneMorale.headquarters) {
      textSections.push(`Siège social : ${personneMorale.headquarters}`);
    }
    
    const textToCopy = textSections.join('\n');
    const notificationLabel = personneMorale.denomination || 'Personne morale copiée';
    
    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'personne_morale_info',
        contenu: textToCopy?.substring(0, 500)
      });
    };

    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([tableHtmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textToCopy], { type: 'text/plain' }),
      });
      
      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(notificationLabel);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          handleCopyToClipboard(textToCopy, tableHtmlContent);
          logCopy();
        });
    } else {
      handleCopyToClipboard(textToCopy, tableHtmlContent);
      logCopy();
    }
  };

  // Fonctions pour gérer les faits
  const resetFaitForm = () => {
    setIsUndetermined(false);
    setIsQualified(false);
    setIsTentative(false);
    setFait('');
    setArticle('');
    setTimeType('fixed');
    setFixedDateTime('');
    setRangeLowDateTime('');
    setRangeHighDateTime('');
    setCountry('');
    setAddress('');
    setDestination('');
    setFaitErrors({});
  };

  const handleOpenFaitDialog = (mode = 'add', itemId = null) => {
    setFaitMode(mode);
    setFaitItemId(itemId);
    
    if (mode === 'edit' && itemId) {
      const faitItem = reportData.faits.find(item => item._id === itemId);
      if (faitItem) {
        setIsUndetermined(faitItem.isUndetermined || false);
        setIsQualified(faitItem.isQualified || false);
        setIsTentative(faitItem.isTentative || false);
        setFait(faitItem.fait || '');
        setArticle(faitItem.article || '');
        setTimeType(faitItem.timeType || 'fixed');
        setFixedDateTime(faitItem.fixedDateTime || '');
        setRangeLowDateTime(faitItem.rangeLowDateTime || '');
        setRangeHighDateTime(faitItem.rangeHighDateTime || '');
        setCountry(faitItem.country || '');
        setAddress(faitItem.address || '');
        setDestination(faitItem.destination || '');
      }
    } else {
      resetFaitForm();
    }
    
    setOpenFaitDialog(true);
  };

  const handleCloseFaitDialog = () => {
    setOpenFaitDialog(false);
    resetFaitForm();
  };

  const validateFaitForm = () => {
    const errors = {};
    
    if (!isUndetermined) {
      if (!fait) {
        errors.fait = 'Le fait est obligatoire';
      }
      if (!address) {
        errors.address = 'L\'adresse est obligatoire';
      }
    }
    
    setFaitErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveFait = async () => {
    if (!validateFaitForm()) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du rapport non disponible');
        return;
      }

      const faitData = {
        isUndetermined,
        isQualified: isUndetermined ? false : isQualified,
        isTentative: isUndetermined ? false : isTentative,
        fait: isUndetermined ? '' : fait,
        article: isUndetermined ? '' : article,
        timeType: isUndetermined ? 'fixed' : timeType,
        fixedDateTime: isUndetermined ? '' : fixedDateTime,
        rangeLowDateTime: isUndetermined ? '' : rangeLowDateTime,
        rangeHighDateTime: isUndetermined ? '' : rangeHighDateTime,
      country: isUndetermined ? '' : country,
      address: isUndetermined ? '' : address,
      destination: isUndetermined ? '' : destination
      };

      const endpoint = `/faits${faitMode === 'edit' ? `/${faitItemId}` : ''}`;
      const url = `/api/report/${reportId}${endpoint}`;
      const method = faitMode === 'add' ? 'post' : 'put';
      
      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'faits',
        id: faitItemId,
        timestamp: Date.now()
      };
      
      if (faitMode === 'add') {
        // Appel API avant mise à jour locale pour avoir le vrai ID
        const response = await axios({
          method,
          url,
          data: faitData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
        
        // Recharger les données pour avoir les vrais IDs
        await reloadReportData();
      } else {
        // Mise à jour locale immédiate pour l'édition
        setReportData(prevData => {
          const updatedItems = [...prevData.faits];
          const index = updatedItems.findIndex(item => item._id === faitItemId);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItems[index],
              ...faitData
            };
          }
          return {
            ...prevData,
            faits: updatedItems
          };
        });
        
        // Appel API pour l'édition
        await axios({
          method,
          url,
          data: faitData,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });
      }
      
      // Log de l'ajout ou modification de fait
      const logActionType = faitMode === 'add' 
        ? LOG_ACTIONS.ELEMENTS_FAIT_ADD 
        : LOG_ACTIONS.ELEMENTS_FAIT_EDIT;
      logAction(logActionType, formatReportNumber(report.number, report.year), {
        mode: faitMode,
        fait: faitData
      });
      
      handleCloseFaitDialog();
    } catch (error) {
      console.error(`Erreur lors de l'${faitMode === 'add' ? 'ajout' : 'édition'} du fait:`, error);
    }
  };

  const handleCopyFait = (faitItem) => {
    if (faitItem.isUndetermined) {
      const textContent = 'Fait non déterminé - procès-verbal subséquent';
      const htmlContent = `<strong>${escapeHtml(textContent)}</strong>`;
      
      const logCopy = () => {
        logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
          type: 'fait',
          contenu: textContent
        });
      };
      
      // Copier avec format HTML et texte
      if (navigator.clipboard && window.ClipboardItem) {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([textContent], { type: 'text/plain' })
        });
        
        navigator.clipboard.write([clipboardItem])
          .then(() => {
            logCopy();
            showCopyNotification(textContent);
          })
          .catch((error) => {
            console.error('Erreur lors de la copie:', error);
            handleCopyToClipboard(textContent, htmlContent);
            logCopy();
          });
      } else {
        handleCopyToClipboard(textContent, htmlContent);
        logCopy();
      }
      return;
    }
    
    let htmlLines = [];
    let textLines = [];
    const indent = '&nbsp;&nbsp;&nbsp;&nbsp;';
    
    // Ligne 1 - En gras
    let line1 = '';
    if (faitItem.isQualified) {
      line1 += 'Fait qualifié de : ';
    }
    if (faitItem.isTentative) {
      line1 += 'Tentative de : ';
    }
    line1 += faitItem.fait;
    htmlLines.push(`<strong>${escapeHtml(line1)}</strong>`);
    textLines.push(line1);
    
    // Ligne 2 si article rempli - avec tabulation
    if (faitItem.article) {
      const line2 = `Article : ${faitItem.article}`;
      htmlLines.push(`${indent}${escapeHtml(line2)}`);
      textLines.push(`\t${line2}`);
    }
    
    // Ligne 3 - Date/heure - avec tabulation
    const line3 = getFaitDateDescription(faitItem);
    htmlLines.push(`${indent}${escapeHtml(line3)}`);
    textLines.push(`\t${line3}`);
    
    // Ligne 4 - Adresse - avec tabulation
    const baseAddress = faitItem.address || '';
    const baseCountry = faitItem.country || '';
    const destinationPart = faitItem.destination ? ` (${faitItem.destination})` : '';
    let addressPart = '';
    if (baseCountry && baseAddress) {
      addressPart = `${baseCountry}, ${baseAddress}${destinationPart}`;
    } else if (baseCountry || baseAddress) {
      addressPart = `${baseCountry}${baseCountry && baseAddress ? ', ' : ''}${baseAddress}${destinationPart}`;
    } else if (faitItem.destination) {
      addressPart = faitItem.destination;
    }
    if (addressPart) {
      const line4 = `À ${addressPart}`;
      htmlLines.push(`${indent}${escapeHtml(line4)}`);
      textLines.push(`\t${line4}`);
    }
    
    // Copier à la fois HTML et texte brut
    const htmlContent = htmlLines.join('<br>');
    const textContent = textLines.join('\n');
    
    const logCopy = () => {
      logAction(LOG_ACTIONS.ELEMENTS_COPY, formatReportNumber(report.number, report.year), {
        type: 'fait',
        contenu: textContent?.substring(0, 500)
      });
    };

    // Utiliser l'API Clipboard pour copier du HTML
    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textContent], { type: 'text/plain' })
      });
      
      navigator.clipboard.write([clipboardItem])
        .then(() => {
          logCopy();
          showCopyNotification(textContent);
        })
        .catch((error) => {
          console.error('Erreur lors de la copie:', error);
          // Fallback sur texte simple
          handleCopyToClipboard(textContent, htmlContent);
          logCopy();
        });
    } else {
      // Fallback pour les navigateurs plus anciens
      handleCopyToClipboard(textContent, htmlContent);
      logCopy();
    }
  };

  // Ajouter une fonction pour recharger les données du rapport
  const reloadReportData = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`/api/report/search`, {
        params: { number: report.number, year: report.year },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setReportData(response.data);
      setUnit(response.data.unit || '');
    } catch (error) {
      console.error('Erreur lors du rechargement des données du rapport:', error);
    }
  };

  // Fonction pour formater la date et heure de début (StartDate)
  const formatStartDate = (dateString) => {
    if (!dateString) return 'Non défini';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      // Format de date: jour mois année
      const dateFormatee = date.toLocaleDateString('fr-FR', {
        weekday: "long",
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      
      // Heure et minutes au format HH:MM
      const heures = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const heureFormatee = `${heures}:${minutes}`;
      
      // Ajouter un "s" à "heure" si l'heure est 2 ou plus
      const pluriel = date.getHours() >= 2 ? 's' : '';
      
      return `${dateFormatee} à ${heureFormatee} heure${pluriel}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date de début:', error);
      return dateString;
    }
  };

  // Fonction pour formater la date de fin (EndDate)
  const formatEndDate = (dateString) => {
    if (!dateString) return 'Non défini';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      // Format de date: jour mois année
      return date.toLocaleDateString('fr-FR', {
        weekday: "long",
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Erreur lors du formatage de la date de fin:', error);
      return dateString;
    }
  };

  // Fonction pour formater la date de naissance (birthdate)
  const formatBirthdate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date de naissance:', error);
      return dateString;
    }
  };

  // Fonction pour calculer l'âge à partir de la date de naissance
  const calculateAge = (dateString) => {
    if (!dateString) return '';
    
    try {
      const birthDate = new Date(dateString);
      if (isNaN(birthDate.getTime())) return '';
      
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age >= 0 ? age : '';
    } catch (error) {
      console.error('Erreur lors du calcul de l\'âge:', error);
      return '';
    }
  };

  const formatGenderAgeLabel = (gender, birthdate) => {
    const genderLabel = gender === 'M' ? 'Masculin' : gender === 'F' ? 'Féminin' : '';
    const ageValue = birthdate ? calculateAge(birthdate) : '';
    if (!genderLabel && (ageValue === '' || ageValue === null || ageValue === undefined)) {
      return '';
    }
    if (ageValue === '' || ageValue === null || ageValue === undefined) {
      return genderLabel;
    }
    const ageNumber = Number(ageValue);
    const plural = ageNumber > 1 ? 'ans' : 'an';
    return genderLabel ? `${genderLabel} - ${ageNumber} ${plural}` : `${ageNumber} ${plural}`;
  };

  // Fonction pour formater la date et heure pour les faits
  const formatFaitDateTime = (dateString) => {
    if (!dateString) return 'Non défini';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      // Jour de la semaine
      const dayName = date.toLocaleDateString('fr-FR', { weekday: 'long' });
      
      // Date au format JJ mois AAAA
      const day = date.getDate();
      const month = date.toLocaleDateString('fr-FR', { month: 'long' });
      const year = date.getFullYear();
      
      // Heure au format HH:MM
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const hoursFormatted = hours.toString().padStart(2, '0');
      
      // Ajouter un "s" à "heure" si l'heure est 2 ou plus
      const pluriel = hours >= 2 ? 's' : '';
      
      return `${dayName} ${day} ${month} ${year} à ${hoursFormatted}:${minutes} heure${pluriel}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date/heure du fait:', error);
      return dateString;
    }
  };

  const formatFaitTimeLabel = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const hoursFormatted = hours.toString().padStart(2, '0');
    const pluriel = hours >= 2 ? 's' : '';
    return `${hoursFormatted}:${minutes} heure${pluriel}`;
  };

  const formatFaitSameDayRange = (lowDateString, highDateString) => {
    if (!lowDateString || !highDateString) return null;
    try {
      const start = new Date(lowDateString);
      const end = new Date(highDateString);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

      const isSameDay =
        start.getFullYear() === end.getFullYear() &&
        start.getMonth() === end.getMonth() &&
        start.getDate() === end.getDate();

      if (!isSameDay) return null;

      const dateLabel = start.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });

      return `Le ${dateLabel} entre ${formatFaitTimeLabel(start)} et ${formatFaitTimeLabel(end)}`;
    } catch (error) {
      console.error('Erreur lors du formatage de l\'intervalle du fait:', error);
      return null;
    }
  };

  const getFaitDateDescription = (faitItem) => {
    if (faitItem.timeType === 'range') {
      const sameDayRangeDescription = formatFaitSameDayRange(
        faitItem.rangeLowDateTime,
        faitItem.rangeHighDateTime
      );
      if (sameDayRangeDescription) {
        return sameDayRangeDescription;
      }
      const lowDate = formatFaitDateTime(faitItem.rangeLowDateTime);
      const highDate = formatFaitDateTime(faitItem.rangeHighDateTime);
      return `Entre le ${lowDate} et le ${highDate}`;
    }
    const fixedDate = formatFaitDateTime(faitItem.fixedDateTime);
    return `Le ${fixedDate}`;
  };

  const escapeHtml = (text) => {
    if (text === null || text === undefined) return '';
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Fonction d'affichage des intervenants
  const renderIntervenants = () => {
    const items = reportData?.intervenants || [];

  return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 0.25
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            flex: 1
          }}
          onClick={() => toggleSection('intervenants')}
          >
            <IconButton 
              size="small"
              sx={{ 
                mr: 0.5,
                transform: sectionExpanded.intervenants ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s',
                color: isDark ? '#4fc3f7' : '#2c3e50',
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#4fc3f7' : '#1a253c', 
              fontWeight: isDark ? 600 : 700,
              textTransform: isDark ? 'none' : 'uppercase',
              letterSpacing: isDark ? 'normal' : '0.03em',
              fontSize: isDark ? undefined : '0.72rem'
            }}>
              Intervenants
            </Typography>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d',
              ml: 1,
              fontWeight: 500
            }}>
              ({items.length})
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
            onClick={() => handleOpenIntervenantDialog()}
            sx={{
              textTransform: 'none',
              color: isDark ? '#4fc3f7' : '#1976d2',
              borderColor: isDark ? '#4fc3f7' : '#1976d2',
              py: 0.25,
              px: 0.75,
              minHeight: '24px',
              '&:hover': {
                borderColor: isDark ? '#29b6f6' : '#1565c0',
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)',
              },
              ...highlightSx,
            }}
          >
            Ajouter
          </Button>
        </Box>
        <Collapse in={sectionExpanded.intervenants} timeout="auto">
          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              bgcolor: isDark ? '#22303e' : '#fff',
              border: isDark ? 'none' : '1px solid #dde3ec',
              borderRadius: 0.5,
              minHeight: '28px'
            }}
          >
            {items.length === 0 ? (
              <Typography variant="body2" sx={{ 
                color: isDark ? '#95a5a6' : '#6c757d', 
                fontStyle: 'italic' 
              }}>
                Aucun élément
              </Typography>
            ) : (
              <List dense disablePadding>
                {items.map((item, index) => (
                  <Box key={item._id || index}>
                    {index > 0 && <Divider component="li" sx={{
                      borderColor: isDark ? '#2d3f4c' : undefined
                    }} />}
                    <ListItem dense disablePadding sx={{ py: 0.25 }}>
                      <ListItemText 
                        primary={`${item.lastname?.toUpperCase() || ''} ${item.firstname || ''}`}
                        secondary={
                          <>
                            <Typography variant="body2" component="span" sx={{
                              color: isDark ? '#95a5a6' : '#6c757d',
                              display: 'block'
                            }}>
                              {convertRankAcronym(item.rank)}
                            </Typography>
                            {item.sign && 
                              <Typography variant="body2" component="span" sx={{ 
                                color: isDark ? '#95a5a6' : '#6c757d', 
                                display: 'block' 
                              }}>
                                <b>Signataire</b>
                              </Typography>
                            }
                          </>
                        }
                        primaryTypographyProps={{ 
                          variant: 'body2', 
                          fontWeight: 500, 
                          color: isDark ? '#ecf0f1' : '#212529' 
                        }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Modifier">
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={() => handleOpenIntervenantDialog('edit', item._id)}
                            sx={{ 
                              mr: 0.2,
                              color: isDark ? '#ecf0f1' : 'inherit'
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={() => handleOpenDeleteDialog('intervenants', item._id)}
                            sx={{ mr: 0.2 }}
                          >
                            <DeleteIcon sx={{ 
                              fontSize: 16, 
                              color: isDark ? '#ff6b6b' : '#d32f2f' 
                            }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copier dans le presse-papier">
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={() => handleCopyIntervenant(item)}
                            sx={{ 
                              color: isDark ? '#4fc3f7' : '#1976d2', 
                              fontSize: 16 
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Box>
                ))}
              </List>
            )}
          </Paper>
        </Collapse>
      </Box>
    );
  };

  // Fonction d'affichage des faits
  const renderFaits = () => {
    const items = reportData?.faits || [];
    
    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 0.25
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            flex: 1
          }}
          onClick={() => toggleSection('faits')}
          >
            <IconButton 
              size="small"
              sx={{ 
                mr: 0.5,
                transform: sectionExpanded.faits ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s',
                color: isDark ? '#4fc3f7' : '#2c3e50',
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#4fc3f7' : '#1a253c', 
              fontWeight: isDark ? 600 : 700,
              textTransform: isDark ? 'none' : 'uppercase',
              letterSpacing: isDark ? 'normal' : '0.03em',
              fontSize: isDark ? undefined : '0.72rem'
            }}>
              Faits
            </Typography>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d',
              ml: 1,
              fontWeight: 500
            }}>
              ({items.length})
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
            onClick={() => handleOpenFaitDialog()}
            sx={{
              textTransform: 'none',
              color: isDark ? '#4fc3f7' : '#1976d2',
              borderColor: isDark ? '#4fc3f7' : '#1976d2',
              py: 0.25,
              px: 0.75,
              minHeight: '24px',
              '&:hover': {
                borderColor: isDark ? '#29b6f6' : '#1565c0',
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)',
              },
              ...highlightSx,
            }}
          >
            Ajouter
          </Button>
        </Box>
        <Collapse in={sectionExpanded.faits} timeout="auto">
          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              bgcolor: isDark ? '#22303e' : '#fff',
              border: isDark ? 'none' : '1px solid #dde3ec',
              borderRadius: 0.5,
              minHeight: '28px'
            }}
          >
          {items.length === 0 ? (
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d', 
              fontStyle: 'italic' 
            }}>
              Aucun élément
            </Typography>
          ) : (
            <List dense disablePadding>
              {items.map((item, index) => (
                <Box key={item._id || index}>
                  {index > 0 && <Divider component="li" sx={{
                    borderColor: isDark ? '#2d3f4c' : undefined
                  }} />}
                  <ListItem dense disablePadding sx={{ py: 0.25 }}>
                    <Box sx={{ width: '100%', pr: 12 }}>
                      {item.isUndetermined ? (
                        <Typography variant="body2" fontWeight={500} sx={{
                          color: isDark ? '#ecf0f1' : '#212529'
                        }}>
                          Fait non déterminé - procès-verbal subséquent
                        </Typography>
                      ) : (
                        <>
                          {/* Ligne 1 */}
                          <Typography variant="body2" fontWeight={500} sx={{
                            color: isDark ? '#ecf0f1' : '#212529'
                          }}>
                            {item.isQualified && 'Fait qualifié de : '}
                            {item.isTentative && 'Tentative de : '}
                            {item.fait}
                          </Typography>
                          
                          {/* Ligne 2 si article rempli */}
                          {item.article && (
                            <Typography variant="body2" sx={{
                              color: isDark ? '#95a5a6' : '#6c757d'
                            }}>
                              Article : {item.article}
                            </Typography>
                          )}
                          
                          {/* Ligne 3 - Date/heure */}
                          <Typography variant="body2" sx={{
                            color: isDark ? '#95a5a6' : '#6c757d'
                          }}>
                            {getFaitDateDescription(item)}
                          </Typography>
                          
                          {/* Ligne 4 - Adresse */}
                          {(item.country || item.address || item.destination) && (
                            <Typography variant="body2" sx={{
                              color: isDark ? '#95a5a6' : '#6c757d'
                            }}>
                              À {item.country || ''}{item.country && item.address ? ', ' : ''}{item.address || ''}{item.destination ? ` (${item.destination})` : ''}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                    
                    <ListItemSecondaryAction>
                      <Tooltip title="Modifier">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleOpenFaitDialog('edit', item._id)}
                          sx={{ 
                            mr: 0.2,
                            color: isDark ? '#ecf0f1' : 'inherit'
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleOpenDeleteDialog('faits', item._id)}
                          sx={{ mr: 0.2 }}
                        >
                          <DeleteIcon sx={{ 
                            fontSize: 16, 
                            color: isDark ? '#ff6b6b' : '#d32f2f' 
                          }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copier dans le presse-papier">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleCopyFait(item)}
                          sx={{ 
                            color: isDark ? '#4fc3f7' : '#1976d2', 
                            fontSize: 16 
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
          </Paper>
        </Collapse>
      </Box>
    );
  };

  // Fonction d'affichage des personnes physiques
  const renderPersonnesPhysiques = () => {
    const items = reportData?.personnesPhysiques || [];
    
    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 0.25
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            flex: 1
          }}
          onClick={() => toggleSection('personnesPhysiques')}
          >
            <IconButton 
              size="small"
              sx={{ 
                mr: 0.5,
                transform: sectionExpanded.personnesPhysiques ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s',
                color: isDark ? '#4fc3f7' : '#2c3e50',
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#4fc3f7' : '#1a253c', 
              fontWeight: isDark ? 600 : 700,
              textTransform: isDark ? 'none' : 'uppercase',
              letterSpacing: isDark ? 'normal' : '0.03em',
              fontSize: isDark ? undefined : '0.72rem'
            }}>
              Personnes physiques
            </Typography>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d',
              ml: 1,
              fontWeight: 500
            }}>
              ({items.length})
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
            onClick={() => handleOpenPersonneDialog()}
            sx={{
              textTransform: 'none',
              color: isDark ? '#4fc3f7' : '#1976d2',
              borderColor: isDark ? '#4fc3f7' : '#1976d2',
              py: 0.25,
              px: 0.75,
              minHeight: '24px',
              '&:hover': {
                borderColor: isDark ? '#29b6f6' : '#1565c0',
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)',
              },
              ...highlightSx,
            }}
          >
            Ajouter
          </Button>
        </Box>
        <Collapse in={sectionExpanded.personnesPhysiques} timeout="auto">
          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              bgcolor: isDark ? '#22303e' : '#fff',
              border: isDark ? 'none' : '1px solid #dde3ec',
              borderRadius: 0.5,
              minHeight: '28px'
            }}
          >
          {items.length === 0 ? (
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d', 
              fontStyle: 'italic' 
            }}>
              Aucun élément
            </Typography>
          ) : (
            <List dense disablePadding>
              {items.map((item, index) => (
                <Box key={item._id || index}>
                  {index > 0 && <Divider component="li" sx={{
                    borderColor: isDark ? '#2d3f4c' : undefined
                  }} />}
                  <ListItem dense disablePadding sx={{ py: 0.25 }}>
                    <Box sx={{ width: '100%', pr: 12 }}>
                      {item.isUnknown ? (
                        <>
                          <Typography variant="body2" fontWeight={600} sx={{
                            color: isDark ? '#95a5a6' : '#6c757d'
                          }}>
                            {item.qualite}
                            {/* Qualités additionnelles */}
                            {item.additionalQualities && item.additionalQualities.length > 0 && (
                              <Typography component="span" variant="body2" sx={{ fontWeight: 400 }}>
                                {' '}({item.additionalQualities.join(', ')})
                              </Typography>
                            )}
                          </Typography>

                          <Typography variant="body2" fontWeight={500} sx={{
                            color: isDark ? '#ecf0f1' : '#212529'
                          }}>
                            Inconnu
                          </Typography>
                        </>
                      ) : (
                        <>
                          {/* Qualité avec sexe et âge */}
                          <Typography variant="body2" fontWeight={600} sx={{
                            color: isDark ? '#95a5a6' : '#6c757d'
                          }}>
                            {item.qualite}
                            {formatGenderAgeLabel(item.gender, item.birthdate) && (
                              <Typography component="span" variant="body2" sx={{ fontWeight: 400 }}>
                                {' '}
                                {formatGenderAgeLabel(item.gender, item.birthdate)}
                              </Typography>
                            )}
                            {/* Qualités additionnelles */}
                            {item.additionalQualities && item.additionalQualities.length > 0 && (
                              <Typography component="span" variant="body2" sx={{ fontWeight: 400 }}>
                                {' '}({item.additionalQualities.join(', ')})
                              </Typography>
                            )}
                          </Typography>

                          {/* Nom, prénom et autres prénoms */}
                          <Typography variant="body2" fontWeight={500} sx={{
                            color: isDark ? '#ecf0f1' : '#212529'
                          }}>
                            {`${item.lastname?.toUpperCase() || ''}${item.firstname ? ' ' + item.firstname : ''}`}
                            {item.otherFirstnames && (
                              <Typography component="span" variant="body2" sx={{
                                color: isDark ? '#95a5a6' : '#7a8a99',
                                ml: 0.5,
                                fontStyle: 'italic'
                              }}>
                                {item.otherFirstnames}
                              </Typography>
                            )}
                          </Typography>

                          {/* Date de naissance et lieu de naissance */}
                          {item.birthdate && 
                            <Typography variant="body2" sx={{
                              color: isDark ? '#95a5a6' : '#6c757d'
                            }}>
                              Ddn : {formatBirthdate(item.birthdate)}
                              {item.birthPlace && ` à ${item.birthPlace}`}
                            </Typography>
                          }

                          {/* Détails supplémentaires en plus petit */}
                          {item.civilStatus && (
                            <Typography variant="caption" sx={{
                              color: isDark ? '#7f8c8d' : '#8898aa',
                              display: 'block',
                              lineHeight: 1.2
                            }}>
                              État civ. : {item.civilStatus}
                            </Typography>
                          )}

                          {item.nationality && (
                            <Typography variant="caption" sx={{
                              color: isDark ? '#7f8c8d' : '#8898aa',
                              display: 'block',
                              lineHeight: 1.2
                            }}>
                              Nat. : {item.nationality}
                            </Typography>
                          )}

                          {item.profession && (
                            <Typography variant="caption" sx={{
                              color: isDark ? '#7f8c8d' : '#8898aa',
                              display: 'block',
                              lineHeight: 1.2
                            }}>
                              Prof. : {item.profession}
                            </Typography>
                          )}

                          {item.residence && (
                            <Typography variant="caption" sx={{
                              color: isDark ? '#7f8c8d' : '#8898aa',
                              display: 'block',
                              lineHeight: 1.2
                            }}>
                              Dom. : {item.residence}
                            </Typography>
                          )}

                          {item.nationalNumber && (
                            <Typography variant="caption" sx={{
                              color: isDark ? '#7f8c8d' : '#8898aa',
                              display: 'block',
                              lineHeight: 1.2
                            }}>
                              NN : {item.nationalNumber}
                            </Typography>
                          )}

                          {item.raavisNumber && (
                            <Typography variant="caption" sx={{
                              color: isDark ? '#7f8c8d' : '#8898aa',
                              display: 'block',
                              lineHeight: 1.2
                            }}>
                              Apfis : {item.raavisNumber}
                            </Typography>
                          )}

                          {(item.photographies || item.empreintes || item.noticeIndividuelle) && (
                            <Typography variant="caption" sx={{
                              color: isDark ? '#7f8c8d' : '#8898aa',
                              display: 'block',
                              lineHeight: 1.2,
                              mt: 0.5
                            }}>
                              {item.photographies && ` [Photo : ${item.photographies}]`}
                              {item.empreintes && ` [Empr. : ${item.empreintes}]`}
                              {item.noticeIndividuelle && ` [Not. : ${item.noticeIndividuelle}]`}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                    <ListItemSecondaryAction>
                      <Tooltip title="Modifier">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleOpenPersonneDialog('edit', item._id)}
                          sx={{ 
                            mr: 0.2,
                            color: isDark ? '#ecf0f1' : 'inherit'
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleOpenDeleteDialog('personnesPhysiques', item._id)}
                          sx={{ mr: 0.2 }}
                        >
                          <DeleteIcon sx={{ 
                            fontSize: 16, 
                            color: isDark ? '#ff6b6b' : '#d32f2f' 
                          }} />
                        </IconButton>
                      </Tooltip>
                      {!item.isUnknown && (
                        <>
                          <Tooltip title="Copier le nom">
                            <IconButton 
                              edge="end" 
                              size="small"
                              onClick={() => handleCopyPersonneNom(item)}
                              sx={{ 
                                mr: 0.2,
                                color: isDark ? '#4fc3f7' : '#1976d2', 
                                fontSize: 16 
                              }}
                            >
                              <PersonIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copier toutes les infos">
                            <IconButton 
                              edge="end" 
                              size="small"
                              onClick={() => handleCopyPersonneInfo(item)}
                              sx={{ 
                                color: isDark ? '#4fc3f7' : '#1976d2', 
                                fontSize: 16 
                              }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
          </Paper>
        </Collapse>
      </Box>
    );
  };

  // Fonction d'affichage des personnes morales
  const renderPersonnesMorales = () => {
    const items = reportData?.personnesMorales || [];
    
    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 0.25
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            flex: 1
          }}
          onClick={() => toggleSection('personnesMorales')}
          >
            <IconButton 
              size="small"
              sx={{ 
                mr: 0.5,
                transform: sectionExpanded.personnesMorales ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s',
                color: isDark ? '#4fc3f7' : '#2c3e50',
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#4fc3f7' : '#1a253c', 
              fontWeight: isDark ? 600 : 700,
              textTransform: isDark ? 'none' : 'uppercase',
              letterSpacing: isDark ? 'normal' : '0.03em',
              fontSize: isDark ? undefined : '0.72rem'
            }}>
              Personnes morales
            </Typography>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d',
              ml: 1,
              fontWeight: 500
            }}>
              ({items.length})
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
            onClick={() => handleOpenPersonneMoraleDialog()}
            sx={{
              textTransform: 'none',
              color: isDark ? '#4fc3f7' : '#1976d2',
              borderColor: isDark ? '#4fc3f7' : '#1976d2',
              py: 0.25,
              px: 0.75,
              minHeight: '24px',
              '&:hover': {
                borderColor: isDark ? '#29b6f6' : '#1565c0',
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)',
              },
              ...highlightSx,
            }}
          >
            Ajouter
          </Button>
        </Box>
        <Collapse in={sectionExpanded.personnesMorales} timeout="auto">
          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              bgcolor: isDark ? '#22303e' : '#fff',
              border: isDark ? 'none' : '1px solid #dde3ec',
              borderRadius: 0.5,
              minHeight: '28px'
            }}
          >
          {items.length === 0 ? (
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d', 
              fontStyle: 'italic' 
            }}>
              Aucun élément
            </Typography>
          ) : (
            <List dense disablePadding>
              {items.map((item, index) => (
                <Box key={item._id || index}>
                  {index > 0 && <Divider component="li" sx={{
                    borderColor: isDark ? '#2d3f4c' : undefined
                  }} />}
                  <ListItem dense disablePadding sx={{ py: 0.25 }}>
                    <Box sx={{ width: '100%', pr: 12 }}>
                      {/* Qualité */}
                      <Typography variant="body2" fontWeight={600} sx={{
                        color: isDark ? '#95a5a6' : '#6c757d'
                      }}>
                        {item.qualite}
                      </Typography>

                      {/* Dénomination */}
                      <Typography variant="body2" fontWeight={500} sx={{
                        color: isDark ? '#ecf0f1' : '#212529'
                      }}>
                        {item.denomination}
                      </Typography>

                      {/* Numéro d'entreprise */}
                      {item.companyNumber && (
                        <Typography variant="body2" sx={{
                          color: isDark ? '#95a5a6' : '#6c757d'
                        }}>
                          N° entreprise : {item.companyNumber}
                        </Typography>
                      )}

                      {/* Siège social */}
                      {item.headquarters && (
                        <Typography variant="body2" sx={{
                          color: isDark ? '#95a5a6' : '#6c757d'
                        }}>
                          Siège : {item.headquarters}
                        </Typography>
                      )}
                    </Box>
                    
                    <ListItemSecondaryAction>
                      <Tooltip title="Modifier">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleOpenPersonneMoraleDialog('edit', item._id)}
                          sx={{ 
                            mr: 0.2,
                            color: isDark ? '#ecf0f1' : 'inherit'
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleOpenDeleteDialog('personnesMorales', item._id)}
                          sx={{ mr: 0.2 }}
                        >
                          <DeleteIcon sx={{ 
                            fontSize: 16, 
                            color: isDark ? '#ff6b6b' : '#d32f2f' 
                          }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copier le nom">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleCopyPersonneMoraleNom(item)}
                          sx={{ 
                            mr: 0.2,
                            color: isDark ? '#4fc3f7' : '#1976d2', 
                            fontSize: 16 
                          }}
                        >
                          <BusinessIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copier toutes les infos">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleCopyPersonneMoraleInfo(item)}
                          sx={{ 
                            color: isDark ? '#4fc3f7' : '#1976d2', 
                            fontSize: 16 
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
          </Paper>
        </Collapse>
      </Box>
    );
  };

  // Fonction d'affichage des objets
  const renderObjets = () => {
    const items = reportData?.objets || [];
    
    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 0.25
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            flex: 1
          }}
          onClick={() => toggleSection('objets')}
          >
            <IconButton 
              size="small"
              sx={{ 
                mr: 0.5,
                transform: sectionExpanded.objets ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s',
                color: isDark ? '#4fc3f7' : '#2c3e50',
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#4fc3f7' : '#1a253c', 
              fontWeight: isDark ? 600 : 700,
              textTransform: isDark ? 'none' : 'uppercase',
              letterSpacing: isDark ? 'normal' : '0.03em',
              fontSize: isDark ? undefined : '0.72rem'
            }}>
              Objets
            </Typography>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d',
              ml: 1,
              fontWeight: 500
            }}>
              ({items.length})
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
            onClick={() => handleOpenObjetDialog()}
            sx={{
              textTransform: 'none',
              color: isDark ? '#4fc3f7' : '#1976d2',
              borderColor: isDark ? '#4fc3f7' : '#1976d2',
              py: 0.25,
              px: 0.75,
              minHeight: '24px',
              '&:hover': {
                borderColor: isDark ? '#29b6f6' : '#1565c0',
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)',
              },
              ...highlightSx,
            }}
          >
            Ajouter
          </Button>
        </Box>
        <Collapse in={sectionExpanded.objets} timeout="auto">
          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              bgcolor: isDark ? '#22303e' : '#fff',
              border: isDark ? 'none' : '1px solid #dde3ec',
              borderRadius: 0.5,
              minHeight: '28px'
            }}
          >
          {items.length === 0 ? (
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d', 
              fontStyle: 'italic' 
            }}>
              Aucun élément
            </Typography>
          ) : (
            <List dense disablePadding>
              {items.map((item, index) => (
                <Box key={item._id || index}>
                  {index > 0 && <Divider component="li" sx={{
                    borderColor: isDark ? '#2d3f4c' : undefined
                  }} />}
                  <ListItem dense disablePadding sx={{ py: 0.25 }}>
                    <ListItemText 
                      primary={
                        <>
                          {item.description}
                          {item.sinNumber && (
                            <Typography component="span" variant="body2" sx={{ 
                              color: isDark ? '#95a5a6' : '#6c757d',
                              ml: 0.5
                            }}>
                              (SIN {item.sinNumber})
                            </Typography>
                          )}
                        </>
                      }
                      primaryTypographyProps={{ 
                        variant: 'body2', 
                        fontWeight: 500, 
                        color: isDark ? '#ecf0f1' : '#212529' 
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Modifier">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleOpenObjetDialog('edit', item._id)}
                          sx={{ 
                            mr: 0.2,
                            color: isDark ? '#ecf0f1' : 'inherit'
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleOpenDeleteDialog('objets', item._id)}
                          sx={{ mr: 0.2 }}
                        >
                          <DeleteIcon sx={{ 
                            fontSize: 16, 
                            color: isDark ? '#ff6b6b' : '#d32f2f' 
                          }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copier dans le presse-papier">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleCopyObjet(item)}
                          sx={{ 
                            color: isDark ? '#4fc3f7' : '#1976d2', 
                            fontSize: 16 
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
          </Paper>
        </Collapse>
      </Box>
    );
  };

  // Fonction d'affichage des véhicules
  const renderVehicules = () => {
    const items = reportData?.vehicules || [];
    
    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 0.25
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            flex: 1
          }}
          onClick={() => toggleSection('vehicules')}
          >
            <IconButton 
              size="small"
              sx={{ 
                mr: 0.5,
                transform: sectionExpanded.vehicules ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s',
                color: isDark ? '#4fc3f7' : '#2c3e50',
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#4fc3f7' : '#1a253c', 
              fontWeight: isDark ? 600 : 700,
              textTransform: isDark ? 'none' : 'uppercase',
              letterSpacing: isDark ? 'normal' : '0.03em',
              fontSize: isDark ? undefined : '0.72rem'
            }}>
              Véhicules
            </Typography>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d',
              ml: 1,
              fontWeight: 500
            }}>
              ({items.length})
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
            onClick={() => handleOpenVehiculeDialog()}
            sx={{
              textTransform: 'none',
              color: isDark ? '#4fc3f7' : '#1976d2',
              borderColor: isDark ? '#4fc3f7' : '#1976d2',
              py: 0.25,
              px: 0.75,
              minHeight: '24px',
              '&:hover': {
                borderColor: isDark ? '#29b6f6' : '#1565c0',
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)',
              },
              ...highlightSx,
            }}
          >
            Ajouter
          </Button>
        </Box>
        <Collapse in={sectionExpanded.vehicules} timeout="auto">
          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              bgcolor: isDark ? '#22303e' : '#fff',
              border: isDark ? 'none' : '1px solid #dde3ec',
              borderRadius: 0.5,
              minHeight: '28px'
            }}
          >
          {items.length === 0 ? (
            <Typography variant="body2" sx={{ 
              color: isDark ? '#95a5a6' : '#6c757d', 
              fontStyle: 'italic' 
            }}>
              Aucun élément
            </Typography>
          ) : (
            <List dense disablePadding>
              {items.map((item, index) => (
                <Box key={item._id || index}>
                  {index > 0 && <Divider component="li" sx={{
                    borderColor: isDark ? '#2d3f4c' : undefined
                  }} />}
                  <ListItem dense disablePadding sx={{ py: 0.25 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={500} sx={{
                          color: isDark ? '#ecf0f1' : '#212529'
                        }}>
                          {item.brand}
                        </Typography>
                      </Box>
                      
                      {item.category && (
                        <Typography variant="body2" sx={{
                          color: isDark ? '#95a5a6' : '#6c757d'
                        }}>
                          Catégorie : {item.category}
                        </Typography>
                      )}
                      
                      {item.plateNumber && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{
                            color: isDark ? '#95a5a6' : '#6c757d'
                          }}>
                            Immat. : {item.plateNumber}
                          </Typography>
                        </Box>
                      )}
                      
                      {item.chassisNumber && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{
                            color: isDark ? '#95a5a6' : '#6c757d'
                          }}>
                            Châssis : {item.chassisNumber}
                          </Typography>
                        </Box>
                      )}
                      
                      {item.nationality && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{
                            color: isDark ? '#95a5a6' : '#6c757d'
                          }}>
                            Nat. : {item.nationality}
                          </Typography>
                        </Box>
                      )}
                      
                      {item.color && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{
                            color: isDark ? '#95a5a6' : '#6c757d'
                          }}>
                            Teinte : {item.color}
                          </Typography>
                        </Box>
                      )}
                      
                      {item.insurance && (
                        <Typography variant="caption" sx={{
                          color: isDark ? '#7f8c8d' : '#8898aa',
                          display: 'block',
                          lineHeight: 1.2
                        }}>
                          Assurance : {item.insurance}
                        </Typography>
                      )}
                      
                      {item.technicalControl && (
                        <Typography variant="caption" sx={{
                          color: isDark ? '#7f8c8d' : '#8898aa',
                          display: 'block',
                          lineHeight: 1.2
                        }}>
                          CT : {item.technicalControl}
                        </Typography>
                      )}
                    </Box>
                    
                    <ListItemSecondaryAction>
                      <Tooltip title="Modifier">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleOpenVehiculeDialog('edit', item._id)}
                          sx={{ 
                            mr: 0.2,
                            color: isDark ? '#ecf0f1' : 'inherit'
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleOpenDeleteDialog('vehicules', item._id)}
                          sx={{ mr: 0.2 }}
                        >
                          <DeleteIcon sx={{ 
                            fontSize: 16, 
                            color: isDark ? '#ff6b6b' : '#d32f2f' 
                          }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copier le véhicule">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleCopyVehiculeSimple(item)}
                          sx={{ 
                            mr: 0.2,
                            color: isDark ? '#4fc3f7' : '#1976d2', 
                            fontSize: 16 
                          }}
                        >
                          <DirectionsCarIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copier toutes les infos">
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleCopyVehiculeInfo(item)}
                          sx={{ 
                            color: isDark ? '#4fc3f7' : '#1976d2', 
                            fontSize: 16 
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
          </Paper>
        </Collapse>
      </Box>
    );
  };

  const handleOpenRepriseDialog = () => {
    setOpenRepriseDialog(true);
    setRepriseNumber('');
    setRepriseYear('');
    setRepriseErrors({});
    setRepriseError('');
    setRepriseOptions({
      redaction: true,
      service: true,
      intervenants: true,
      faits: true,
      personnesPhysiques: true,
      personnesMorales: true,
      objets: true,
      vehicules: true,
      rubriques: true
    });
  };

  const handleCloseRepriseDialog = () => {
    setOpenRepriseDialog(false);
  };

  const validateRepriseForm = () => {
    const errors = {};
    
    if (!repriseNumber) {
      errors.number = 'Le numéro est obligatoire';
    } else if (repriseNumber.length !== 6 || !/^\d+$/.test(repriseNumber)) {
      errors.number = 'Le numéro doit contenir 6 chiffres';
    }
    
    if (!repriseYear) {
      errors.year = 'L\'année est obligatoire';
    } else if (repriseYear.length !== 4 || !/^\d+$/.test(repriseYear)) {
      errors.year = 'L\'année doit contenir 4 chiffres';
    }
    
    setRepriseErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRepriseOptionChange = (option) => (event) => {
    setRepriseOptions(prev => ({
      ...prev,
      [option]: event.target.checked
    }));
  };

  // Gestion du dialog de modification du type de PV
  const handleOpenTypeDialog = () => {
    setOpenTypeDialog(true);
    setReportType(reportData?.reportType || 'subsequent');
    setIndice(reportData?.indice || '');
    setIndiceError('');
  };

  const handleCloseTypeDialog = () => {
    setOpenTypeDialog(false);
  };

  const validateIndice = (value) => {
    if (reportType === 'initial') {
      if (!value || value.trim() === '') {
        setIndiceError('L\'indice est obligatoire pour un PV initial');
        return false;
      }
      if (!/^\d{2}$/.test(value)) {
        setIndiceError('L\'indice doit être composé de 2 chiffres');
        return false;
      }
    }
    setIndiceError('');
    return true;
  };

  const handleSaveReportType = async () => {
    if (!validateIndice(indice)) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        console.error('ID du document non disponible');
        return;
      }

      const updatedData = {
        reportType,
        indice: reportType === 'initial' ? indice : ''
      };

      // Enregistrer cette action pour éviter les doublons avec WebSocket
      lastActionRef.current = {
        type: 'reportType',
        id: null,
        timestamp: Date.now()
      };

      // Mise à jour locale immédiate
      setReportData(prevData => ({
        ...prevData,
        ...updatedData
      }));
      setReportType(updatedData.reportType);
      setIndice(updatedData.indice);

      // Émission via WebSocket pour informer les autres utilisateurs
      if (socket) {
        socket.emit('updateReport', {
          reportId: `${report.number}-${report.year}`,
          ...updatedData,
          number: report.number,
          year: report.year
        });
      }

      // Mise à jour via API
      await axios.put(
        `/api/report/${reportId}/type`,
        updatedData,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      
      // Log de la modification de la structure PV
      logAction(LOG_ACTIONS.ELEMENTS_STRUCTURE_UPDATE, formatReportNumber(report.number, report.year), {
        ancienReportType: reportData?.reportType || null,
        ancienIndice: reportData?.indice || null,
        nouveauReportType: updatedData.reportType,
        nouveauIndice: updatedData.indice
      });
      
      handleCloseTypeDialog();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du type de PV:', error);
      handleCloseTypeDialog();
    }
  };

  const handleReprise = async () => {
    if (!validateRepriseForm()) return;
    
    try {
      setRepriseLoading(true);
      setRepriseError('');
      
      const token = sessionStorage.getItem('authToken');
      const reportId = reportData?._id;
      
      if (!reportId) {
        setRepriseError('ID du rapport actuel non disponible');
        setRepriseLoading(false);
        return;
      }
      
      // Récupérer le rapport source
      const sourceResponse = await axios.get(
        `/api/report/search?number=${repriseNumber}&year=${repriseYear}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      const sourceReport = sourceResponse.data;
      
      // Vérifier si le rapport existe et est public
      if (!sourceReport) {
        setRepriseError('Rapport non trouvé');
        setRepriseLoading(false);
        return;
      }
      
      if (sourceReport.access?.type !== 'public') {
        setRepriseError('Vous ne pouvez importer les encodages que si le document selectionné n\'est pas restreint');
        setRepriseLoading(false);
        return;
      }
      
      // Mise à jour locale immédiate pour une meilleure expérience utilisateur
      setReportData(prevData => {
        const updatedData = { ...prevData };
        
        // Rédaction
        if (repriseOptions.redaction && sourceReport.StartDate) {
          updatedData.StartDate = sourceReport.StartDate;
          updatedData.EndDate = sourceReport.EndDate || '';
          
          // Mettre à jour les états locaux également
          setStartDate(sourceReport.StartDate);
          setEndDate(sourceReport.EndDate || '');
        }
        
        // Service
        if (repriseOptions.service && sourceReport.unit) {
          updatedData.unit = sourceReport.unit;
          
          // Mettre à jour l'état local
          setUnit(sourceReport.unit);
        }
        
        // Intervenants
        if (repriseOptions.intervenants && sourceReport.intervenants) {
          const newIntervenants = sourceReport.intervenants.map(item => ({
            ...item,
            _id: `temp-${Date.now()}-${Math.random()}`
          }));
          updatedData.intervenants = [...(prevData.intervenants || []), ...newIntervenants];
        }
        
        // Faits
        if (repriseOptions.faits && sourceReport.faits) {
          const newFaits = sourceReport.faits.map(item => ({
            ...item,
            _id: `temp-${Date.now()}-${Math.random()}`
          }));
          updatedData.faits = [...(prevData.faits || []), ...newFaits];
        }
        
        // Personnes concernées
        if (repriseOptions.personnesPhysiques && sourceReport.personnesPhysiques) {
          const newPersonnes = sourceReport.personnesPhysiques.map(item => ({
            ...item,
            _id: `temp-${Date.now()}-${Math.random()}`
          }));
          updatedData.personnesPhysiques = [...(prevData.personnesPhysiques || []), ...newPersonnes];
        }
        
        // Personnes morales
        if (repriseOptions.personnesMorales && sourceReport.personnesMorales) {
          const newPersonnesMorales = sourceReport.personnesMorales.map(item => ({
            ...item,
            _id: `temp-${Date.now()}-${Math.random()}`
          }));
          updatedData.personnesMorales = [...(prevData.personnesMorales || []), ...newPersonnesMorales];
        }
        
        // Objets
        if (repriseOptions.objets && sourceReport.objets) {
          const newObjets = sourceReport.objets.map(item => ({
            ...item,
            _id: `temp-${Date.now()}-${Math.random()}`
          }));
          updatedData.objets = [...(prevData.objets || []), ...newObjets];
        }
        
        // Véhicules
        if (repriseOptions.vehicules && sourceReport.vehicules) {
          const newVehicules = sourceReport.vehicules.map(item => ({
            ...item,
            _id: `temp-${Date.now()}-${Math.random()}`
          }));
          updatedData.vehicules = [...(prevData.vehicules || []), ...newVehicules];
        }
        
        // Rubriques
        if (repriseOptions.rubriques && sourceReport.rubrics) {
          const newRubrics = sourceReport.rubrics.map(item => ({
            ...item,
            _id: `temp-${Date.now()}-${Math.random()}`
          }));
          updatedData.rubrics = [...(prevData.rubrics || []), ...newRubrics];
        }
        
        return updatedData;
      });
      
      // Mettre à jour sur le serveur
      const updateData = async () => {
        // Rédaction
        if (repriseOptions.redaction && sourceReport.StartDate) {
          await axios.put(
            `/api/report/${reportId}/redaction`,
            { 
              StartDate: sourceReport.StartDate,
              EndDate: sourceReport.EndDate || ''
            },
            { 
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              withCredentials: true
            }
          );
        }
        
        // Service
        if (repriseOptions.service && sourceReport.unit) {
          await axios.put(
            `/api/report/${reportId}/unit`,
            { unit: sourceReport.unit },
            { 
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              withCredentials: true
            }
          );
        }
        
        // Fonction pour ajouter des éléments
        const addElements = async (elements, path) => {
          if (elements.length === 0) return;
          
          for (const element of elements) {
            const { _id, addedBy, addedAt, ...elementData } = element;
            await axios.post(
              `/api/report/${reportId}/${path}`,
              elementData,
              { 
                headers: { 
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                withCredentials: true
              }
            );
          }
        };
        
        // Ajouter les éléments sélectionnés
        if (repriseOptions.intervenants && sourceReport.intervenants) {
          await addElements(sourceReport.intervenants, 'intervenants');
        }
        
        if (repriseOptions.faits && sourceReport.faits) {
          await addElements(sourceReport.faits, 'faits');
        }
        
        if (repriseOptions.personnesPhysiques && sourceReport.personnesPhysiques) {
          await addElements(sourceReport.personnesPhysiques, 'personnes-physiques');
        }
        
        if (repriseOptions.personnesMorales && sourceReport.personnesMorales) {
          await addElements(sourceReport.personnesMorales, 'personnes-morales');
        }
        
        if (repriseOptions.objets && sourceReport.objets) {
          await addElements(sourceReport.objets, 'objets');
        }
        
        if (repriseOptions.vehicules && sourceReport.vehicules) {
          await addElements(sourceReport.vehicules, 'vehicules');
        }
        
        // Ajouter les rubriques
        if (repriseOptions.rubriques && sourceReport.rubrics) {
          // Récupérer les rubriques actuelles du rapport cible
          const currentReportResponse = await axios.get(
            `/api/report/search`,
            {
              params: { number: report.number, year: report.year },
              headers: { Authorization: `Bearer ${token}` },
              withCredentials: true
            }
          );
          
          if (currentReportResponse.data && currentReportResponse.data.rubrics) {
            // Créer des versions des rubriques sources avec de nouveaux IDs
            const newRubrics = sourceReport.rubrics.map(rubric => ({
              ...rubric,
              id: `rubric-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
              position: (currentReportResponse.data.rubrics.length || 0) + 1 + (sourceReport.rubrics.indexOf(rubric))
            }));
            
            // Fusionner avec les rubriques existantes
            const updatedRubrics = [...currentReportResponse.data.rubrics, ...newRubrics];
            
            // Mettre à jour via API avec toutes les rubriques
            await axios.put(
              `/api/report/${reportId}/rubrics`,
              { rubrics: updatedRubrics },
              { 
                headers: { 
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                withCredentials: true
              }
            );
          }
        }
      };

      
      await updateData();
      
      // Recharger les données pour avoir les vrais IDs générés par le serveur
      await reloadReportData();

      // Log de la reprise des éléments
      const elementsRepris = [];
      if (repriseOptions.redaction) elementsRepris.push('rédaction');
      if (repriseOptions.service) elementsRepris.push('service');
      if (repriseOptions.intervenants) elementsRepris.push('intervenants');
      if (repriseOptions.faits) elementsRepris.push('faits');
      if (repriseOptions.personnesPhysiques) elementsRepris.push('personnes physiques');
      if (repriseOptions.personnesMorales) elementsRepris.push('personnes morales');
      if (repriseOptions.objets) elementsRepris.push('objets');
      if (repriseOptions.vehicules) elementsRepris.push('véhicules');
      if (repriseOptions.rubriques) elementsRepris.push('rubriques');
      
      logAction(LOG_ACTIONS.ELEMENTS_REPRISE, formatReportNumber(report.number, report.year), {
        sourceReport: `${repriseNumber}/${repriseYear}`,
        elementsRepris: elementsRepris.join(', ')
      });
      
      setRepriseLoading(false);
      handleCloseRepriseDialog();
    } catch (error) {
      console.error('Erreur lors de la reprise des éléments:', error);
      let errorMessage = 'Une erreur est survenue lors de la reprise';
      
      if (error.response) {
        errorMessage = error.response.data?.message || `Erreur ${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
        errorMessage = 'Impossible de communiquer avec le serveur. Vérifiez votre connexion.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setRepriseError(errorMessage);
      setRepriseLoading(false);
    }
  };

  if (loading && !reportData) {
    return (
      <Box sx={{ 
        p: 0.5,
        backgroundColor: isDark ? '#1a252f' : 'inherit',
        color: isDark ? '#ecf0f1' : 'inherit'
      }}>
        Chargement...
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 0.25,
      backgroundColor: isDark ? '#1a252f' : 'inherit',
      minHeight: '100%'
    }}>
      <Stack spacing={isDark ? 0.5 : 1.25}>
        {/* Service */}
        <Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 0.25
          }}>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#4fc3f7' : '#1a253c', 
              fontWeight: isDark ? 600 : 700,
              textTransform: isDark ? 'none' : 'uppercase',
              letterSpacing: isDark ? 'normal' : '0.03em',
              fontSize: isDark ? undefined : '0.72rem'
            }}>
              Structure
            </Typography>
            <Button
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              variant="outlined"
              size="small"
              onClick={handleOpenTypeDialog}
              sx={{
                textTransform: 'none',
                color: isDark ? '#4fc3f7' : '#1976d2',
                borderColor: isDark ? '#4fc3f7' : '#1976d2',
                py: 0.25,
                px: 0.75,
                minHeight: '24px',
                '&:hover': {
                  borderColor: isDark ? '#29b6f6' : '#1565c0',
                  backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                },
                ...highlightSx,
              }}
            >
              Modifier
            </Button>
          </Box>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              bgcolor: isDark ? '#22303e' : '#fff',
              border: isDark ? 'none' : '1px solid #dde3ec',
              borderRadius: 0.5,
              minHeight: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="body2" sx={{ color: isDark ? '#ecf0f1' : '#495057' }}>
              Procès-verbal {reportData?.reportType || 'Non défini'}
              {reportData?.reportType === 'initial' && (
                <>
                  <br/>
                  {reportData?.indice ? (
                    <>
                      Indicé <strong>{reportData.indice}</strong> - {getIndiceData(reportData.indice).name}
                    </>
                  ) : (
                    'Non défini'
                  )}
                </>
              )}
            </Typography>
          </Paper>
        </Box>

        {/* Redaction */}
        <Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 0.25
          }}>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#4fc3f7' : '#1a253c', 
              fontWeight: isDark ? 600 : 700,
              textTransform: isDark ? 'none' : 'uppercase',
              letterSpacing: isDark ? 'normal' : '0.03em',
              fontSize: isDark ? undefined : '0.72rem'
            }}>
              Rédaction
            </Typography>
            <Button
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              variant="outlined"
              size="small"
              onClick={handleOpenRedactionDialog}
              sx={{
                textTransform: 'none',
                color: isDark ? '#4fc3f7' : '#1976d2',
                borderColor: isDark ? '#4fc3f7' : '#1976d2',
                py: 0.25,
                px: 0.75,
                minHeight: '24px',
                '&:hover': {
                  borderColor: isDark ? '#29b6f6' : '#1565c0',
                  backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                },
                ...highlightSx,
              }}
            >
              Modifier
            </Button>
          </Box>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              bgcolor: isDark ? '#22303e' : '#fff',
              border: isDark ? 'none' : '1px solid #dde3ec',
              borderRadius: 0.5,
              minHeight: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexDirection: 'column'
            }}
          >
            <Typography variant="body2" sx={{ 
              color: isDark ? '#ecf0f1' : '#495057', 
              width: '100%', 
              display: 'flex', 
              justifyContent: 'space-between' 
            }}>
              <span>Débuté le {formatStartDate(reportData?.StartDate)}</span>
            </Typography>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#ecf0f1' : '#495057', 
              width: '100%', 
              display: 'flex', 
              justifyContent: 'space-between' 
            }}>
              <span>Clos le {formatEndDate(reportData?.EndDate)}</span>
            </Typography>
            <Box sx={{ mt: 1, width: '100%' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSetEndDateToday}
                sx={{ 
                  width: '100%',
                  textTransform: 'none',
                  color: isDark ? '#4fc3f7' : '#1976d2',
                  borderColor: isDark ? '#4fc3f7' : '#1976d2',
                  py: 0.25,
                  px: 0.75,
                  minHeight: '24px',
                  fontSize: '0.75rem',
                  '&:hover': {
                    borderColor: isDark ? '#29b6f6' : '#1565c0',
                    backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                Date de clôture aujourd'hui
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Service */}
        <Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 0.25
          }}>
            <Typography variant="body2" sx={{ 
              color: isDark ? '#4fc3f7' : '#1a253c', 
              fontWeight: isDark ? 600 : 700,
              textTransform: isDark ? 'none' : 'uppercase',
              letterSpacing: isDark ? 'normal' : '0.03em',
              fontSize: isDark ? undefined : '0.72rem'
            }}>
              Service
            </Typography>
            <Button
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              variant="outlined"
              size="small"
              onClick={handleOpenUnitDialog}
              sx={{
                textTransform: 'none',
                color: isDark ? '#4fc3f7' : '#1976d2',
                borderColor: isDark ? '#4fc3f7' : '#1976d2',
                py: 0.25,
                px: 0.75,
                minHeight: '24px',
                '&:hover': {
                  borderColor: isDark ? '#29b6f6' : '#1565c0',
                  backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                },
                ...highlightSx,
              }}
            >
              Modifier
            </Button>
          </Box>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 0.5, 
              bgcolor: isDark ? '#22303e' : '#fff',
              border: isDark ? 'none' : '1px solid #dde3ec',
              borderRadius: 0.5,
              minHeight: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="body2" sx={{ color: isDark ? '#ecf0f1' : '#495057' }}>
              {unit || 'Non défini'}
            </Typography>
            <Tooltip title="Copier dans le presse-papier">
              <IconButton 
                size="small" 
                onClick={() => handleCopyToClipboard(unit)}
                disabled={!unit}
                sx={{ 
                  color: isDark ? '#4fc3f7' : '#1976d2',
                  '&:hover': { 
                    backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(25, 118, 210, 0.04)' 
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Paper>
        </Box>

        {/* Intervenants */}
        {renderIntervenants()}

        {/* Faits */}
        {renderFaits()}

        {/* Personnes physiques */}
        {renderPersonnesPhysiques()}

        {/* Personnes morales */}
        {renderPersonnesMorales()}

        {/* Véhicules */}
        {renderVehicules()}

        {/* Objets */}
        {renderObjets()}
      </Stack>

      {/* Dialog de modification d'unité */}
      <Dialog 
        open={openUnitDialog} 
        onClose={handleCloseUnitDialog} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#22303e' : undefined,
            color: isDark ? '#ecf0f1' : undefined
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600, 
          fontSize: '1rem', 
          pb: 1,
          color: isDark ? '#ecf0f1' : 'inherit'
        }}>
          Modifier le service
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box>
            <TextField
              autoFocus
              margin="dense"
              label="Unité"
              type="text"
              fullWidth
              variant="outlined"
              value={unit}
              onChange={(e) => {
                setUnit(e.target.value);
                validateUnit(e.target.value);
              }}
              error={!!unitError}
              helperText={unitError}
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: isDark ? '#1a252f' : 'inherit',
                  color: isDark ? '#ecf0f1' : 'inherit',
                  '& fieldset': {
                    borderColor: isDark ? '#2d3f4c' : undefined
                  },
                  '&:hover fieldset': {
                    borderColor: isDark ? '#4fc3f7' : undefined
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDark ? '#4fc3f7' : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: isDark ? '#bdc3c7' : undefined,
                  '&.Mui-focused': {
                    color: isDark ? '#4fc3f7' : undefined
                  }
                },
                '& .MuiFormHelperText-root': {
                  color: isDark ? '#95a5a6' : undefined
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleCloseUnitDialog}
            sx={{ color: isDark ? '#bdc3c7' : 'text.secondary' }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleSaveUnit}
            variant="contained"
            disabled={!unit || !!unitError}
            sx={{
              backgroundColor: isDark ? '#4fc3f7' : undefined,
              color: isDark ? '#000' : undefined,
              '&:hover': {
                backgroundColor: isDark ? '#29b6f6' : undefined
              }
            }}
          >
            Modifier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour ajouter/modifier un intervenant */}
      <Dialog open={openIntervenantDialog} onClose={handleCloseIntervenantDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          {intervenantMode === 'add' ? 'Ajouter un ' : 'Modifier l\''}intervenant
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box>
            <ToggleButtonGroup
              value={isExternalMember ? 'external' : 'listed'}
              exclusive
              onChange={(_, newValue) => {
                if (!newValue) return;
                const external = newValue === 'external';
                setIsExternalMember(external);
                setIntervenantErrors({});
                if (!external) {
                  syncFlagsFromUser(selectedUserId, { opj: false, opa: false });
                }
              }}
              fullWidth
              sx={{ mb: 2 }}
            >
              <ToggleButton value="listed">Membre listé</ToggleButton>
              <ToggleButton value="external">Membre extérieur</ToggleButton>
            </ToggleButtonGroup>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isSignataire}
                  onChange={(e) => setIsSignataire(e.target.checked)}
                />
              }
              label="Signataire"
              sx={{ mb: 1 }}
            />

            <FormGroup row sx={{ mb: isExternalMember ? 1.5 : 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isOPJ}
                    onChange={(e) => setIsOPJ(e.target.checked)}
                    disabled={!isExternalMember}
                  />
                }
                label="OPJ"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isOPA}
                    onChange={(e) => setIsOPA(e.target.checked)}
                    disabled={!isExternalMember}
                  />
                }
                label="OPA"
              />
            </FormGroup>
            {!isExternalMember && (
              <FormHelperText sx={{ mt: -1, mb: 1 }}>
                Les statuts OPJ/OPA sont déterminés automatiquement selon l'utilisateur choisi.
              </FormHelperText>
            )}

            {isExternalMember ? (
              <>
                <FormControl fullWidth margin="dense" size="small" error={!!intervenantErrors.rank}>
                  <InputLabel>Grade</InputLabel>
                  <Select
                    value={rank}
                    label="Grade"
                    onChange={(e) => setRank(e.target.value)}
                  >
                    {gradesList.map((grade) => (
                      <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                    ))}
                  </Select>
                  {intervenantErrors.rank && <FormHelperText>{intervenantErrors.rank}</FormHelperText>}
                </FormControl>

                <TextField
                  margin="dense"
                  label="Nom"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  error={!!intervenantErrors.lastname}
                  helperText={intervenantErrors.lastname}
                  size="small"
                />

                <TextField
                  margin="dense"
                  label="Prénom"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  error={!!intervenantErrors.firstname}
                  helperText={intervenantErrors.firstname}
                  size="small"
                />
              </>
            ) : (
              <FormControl fullWidth margin="dense" size="small" error={!!intervenantErrors.userId}>
                <InputLabel>Intervenant</InputLabel>
                <Select
                  value={selectedUserId}
                  label="Utilisateur"
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedUserId(value);
                    syncFlagsFromUser(value, { opj: false, opa: false });
                  }}
                >
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.lastname.toUpperCase()} {user.firstname} ({user.rank || 'Membre'})
                    </MenuItem>
                  ))}
                </Select>
                {intervenantErrors.userId && <FormHelperText>{intervenantErrors.userId}</FormHelperText>}
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleCloseIntervenantDialog}
            sx={{ color: 'text.secondary' }}
          >
            Quitter
          </Button>
          <Button 
            onClick={handleSaveIntervenant}
            variant="contained"
            disabled={isExternalMember ? (!rank || !lastname || !firstname) : !selectedUserId}
          >
            {intervenantMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour ajouter/modifier une personne physique */}
      <Dialog open={openPersonneDialog} onClose={handleClosePersonneDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          {personneMode === 'add' ? 'Ajouter une' : 'Modifier la'} personne physique
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box>
            <Autocomplete
              freeSolo
              options={personneQualitesList}
              value={personneQualite}
              onChange={(event, newValue) => {
                if (newValue && !personneQualitesList.includes(newValue)) {
                  setPersonneQualitesList(prev => [...prev, newValue]);
                }
                setPersonneQualite(newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                setPersonneQualite(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Qualité"
                  margin="dense"
                  size="small"
                  error={!!personneErrors.qualite}
                  helperText={personneErrors.qualite}
                  fullWidth
                />
              )}
            />

            <FormControl fullWidth margin="dense" size="small">
              <InputLabel>Qualités additionnelles</InputLabel>
              <Select
                multiple
                value={additionalQualities}
                label="Qualités additionnelles"
                onChange={(e) => setAdditionalQualities(e.target.value)}
                renderValue={(selected) => selected.join(', ')}
              >
                <MenuItem value="absent">
                  <Checkbox checked={additionalQualities.indexOf('absent') > -1} />
                  <ListItemText primary="absent" />
                </MenuItem>
                <MenuItem value="blessé léger">
                  <Checkbox checked={additionalQualities.indexOf('blessé léger') > -1} />
                  <ListItemText primary="blessé léger" />
                </MenuItem>
                <MenuItem value="blessé grave">
                  <Checkbox checked={additionalQualities.indexOf('blessé grave') > -1} />
                  <ListItemText primary="blessé grave" />
                </MenuItem>
                <MenuItem value="décédé">
                  <Checkbox checked={additionalQualities.indexOf('décédé') > -1} />
                  <ListItemText primary="décédé" />
                </MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isUnknown}
                  onChange={(e) => setIsUnknown(e.target.checked)}
                />
              }
              label="Individu inconnu"
              sx={{ mt: 1, mb: 1 }}
            />

            {!isUnknown && (
              <>
                <TextField
                  margin="dense"
                  label="Nom"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={personneLastname}
                  onChange={(e) => setPersonneLastname(e.target.value)}
                  error={!!personneErrors.lastname}
                  helperText={personneErrors.lastname}
                  size="small"
                />

                <TextField
                  margin="dense"
                  label="Prénom"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={personneFirstname}
                  onChange={(e) => setPersonneFirstname(e.target.value)}
                  size="small"
                />

                <TextField
                  margin="dense"
                  label="Autres prénoms"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={otherFirstnames}
                  onChange={(e) => setOtherFirstnames(e.target.value)}
                  size="small"
                />

                <FormControl component="fieldset" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
                    Sexe
                  </Typography>
                  <RadioGroup
                    row
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <FormControlLabel value="M" control={<Radio />} label="Masculin" />
                    <FormControlLabel value="F" control={<Radio />} label="Féminin" />
                  </RadioGroup>
                </FormControl>

                <TextField
                  margin="dense"
                  label="Lieu de naissance"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={birthPlace}
                  onChange={(e) => setBirthPlace(e.target.value)}
                  size="small"
                />

                <TextField
                  margin="dense"
                  label="Date de naissance"
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={birthdate}
                  onChange={(e) => setBirthdate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                />

                <TextField
                  margin="dense"
                  label="État civil"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={civilStatus}
                  onChange={(e) => setCivilStatus(e.target.value)}
                  size="small"
                />

                <TextField
                  margin="dense"
                  label="Nationalité"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={nationality}
                  onChange={(e) => setNationality(e.target.value)}
                  size="small"
                />

                <TextField
                  margin="dense"
                  label="Profession"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                  size="small"
                />

                <TextField
                  margin="dense"
                  label="Domicile"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={residence}
                  onChange={(e) => setResidence(e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                />

                <TextField
                  margin="dense"
                  label="Numéro national"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={nationalNumber}
                  onChange={(e) => setNationalNumber(e.target.value)}
                  size="small"
                />

                <TextField
                  margin="dense"
                  label="Numéro APFIS"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={raavisNumber}
                  onChange={(e) => setRaavisNumber((e.target.value || '').toUpperCase())}
                  size="small"
                />

                <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                  Triptyque :
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ flex: '1 1 auto', minWidth: 120 }}>
                    <InputLabel>Photographies</InputLabel>
                    <Select
                      value={photographies}
                      label="Photographies"
                      onChange={(e) => setPhotographies(e.target.value)}
                    >
                      <MenuItem value="">Ne pas mentionner</MenuItem>
                      <MenuItem value="Oui">Oui</MenuItem>
                      <MenuItem value="Non">Non</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ flex: '1 1 auto', minWidth: 120 }}>
                    <InputLabel>Empreintes</InputLabel>
                    <Select
                      value={empreintes}
                      label="Empreintes"
                      onChange={(e) => setEmpreintes(e.target.value)}
                    >
                      <MenuItem value="">Ne pas mentionner</MenuItem>
                      <MenuItem value="Oui">Oui</MenuItem>
                      <MenuItem value="Non">Non</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ flex: '1 1 auto', minWidth: 120 }}>
                    <InputLabel>Notice individuelle</InputLabel>
                    <Select
                      value={noticeIndividuelle}
                      label="Notice individuelle"
                      onChange={(e) => setNoticeIndividuelle(e.target.value)}
                    >
                      <MenuItem value="">Ne pas mentionner</MenuItem>
                      <MenuItem value="Oui">Oui</MenuItem>
                      <MenuItem value="Non">Non</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleClosePersonneDialog}
            sx={{ color: 'text.secondary' }}
          >
            Quitter
          </Button>
          <Button 
            onClick={handleSavePersonne}
            variant="contained"
            disabled={!personneQualite || (!isUnknown && !personneLastname)}
          >
            {personneMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour ajouter/modifier un objet */}
      <Dialog open={openObjetDialog} onClose={handleCloseObjetDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          {objetMode === 'add' ? 'Ajouter un' : 'Modifier l\''}objet
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box>
            <TextField
              margin="dense"
              label="Description et nombre"
              type="text"
              fullWidth
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={!!objetErrors.description}
              helperText={objetErrors.description}
              size="small"
            />

            <TextField
              margin="dense"
              label="Numéro SIN"
              type="text"
              fullWidth
              variant="outlined"
              value={sinNumber}
              onChange={(e) => setSinNumber(e.target.value)}
              helperText="Facultatif"
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleCloseObjetDialog}
            sx={{ color: 'text.secondary' }}
          >
            Quitter
          </Button>
          <Button 
            onClick={handleSaveObjet}
            variant="contained"
            disabled={!description}
          >
            {objetMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour ajouter/modifier un véhicule */}
      <Dialog open={openVehiculeDialog} onClose={handleCloseVehiculeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          {vehiculeMode === 'add' ? 'Ajouter un' : 'Modifier le'} véhicule
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box>
            <TextField
              margin="dense"
              label="Marque et modèle"
              type="text"
              fullWidth
              variant="outlined"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              error={!!vehiculeErrors.brand}
              helperText={vehiculeErrors.brand}
              size="small"
            />
            <TextField
              margin="dense"
              label="Catégorie"
              type="text"
              fullWidth
              variant="outlined"
              value={vehiculeCategory}
              onChange={(e) => setVehiculeCategory(e.target.value)}
              size="small"
            />

            <TextField
              margin="dense"
              label="Immatriculation"
              type="text"
              fullWidth
              variant="outlined"
              value={plateNumber}
              onChange={(e) => setPlateNumber((e.target.value || '').toUpperCase())}
              size="small"
            />

            <TextField
              margin="dense"
              label="Numéro de châssis/cadre"
              type="text"
              fullWidth
              variant="outlined"
              value={chassisNumber}
              onChange={(e) => setChassisNumber((e.target.value || '').toUpperCase())}
              size="small"
            />
            
            <TextField
              margin="dense"
              label="Nationalité"
              type="text"
              fullWidth
              variant="outlined"
              value={vehiculeNationality}
              onChange={(e) => setVehiculeNationality(e.target.value)}
              size="small"
            />
            
            <TextField
              margin="dense"
              label="Teinte"
              type="text"
              fullWidth
              variant="outlined"
              value={vehiculeColor}
              onChange={(e) => setVehiculeColor(e.target.value)}
              helperText="Facultatif"
              size="small"
            />

            <TextField
              margin="dense"
              label="Assurance"
              type="text"
              fullWidth
              variant="outlined"
              value={insurance}
              onChange={(e) => setInsurance(e.target.value)}
              helperText="Facultatif"
              size="small"
            />

            <TextField
              margin="dense"
              label="Contrôle technique"
              type="text"
              fullWidth
              variant="outlined"
              value={technicalControl}
              onChange={(e) => setTechnicalControl(e.target.value)}
              helperText="Facultatif"
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleCloseVehiculeDialog}
            sx={{ color: 'text.secondary' }}
          >
            Quitter
          </Button>
          <Button 
            onClick={handleSaveVehicule}
            variant="contained"
            disabled={!brand}
          >
            {vehiculeMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour ajouter/modifier une personne morale */}
      <Dialog open={openPersonneMoraleDialog} onClose={handleClosePersonneMoraleDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          {personneMoraleMode === 'add' ? 'Ajouter une' : 'Modifier la'} personne morale
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box>
            <Autocomplete
              freeSolo
              options={personneMoraleQualitesList}
              value={personneMoraleQualite}
              onChange={(event, newValue) => {
                if (newValue && !personneMoraleQualitesList.includes(newValue)) {
                  setPersonneMoraleQualitesList(prev => [...prev, newValue]);
                }
                setPersonneMoraleQualite(newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                setPersonneMoraleQualite(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Qualité"
                  margin="dense"
                  size="small"
                  error={!!personneMoraleErrors.qualite}
                  helperText={personneMoraleErrors.qualite}
                  fullWidth
                />
              )}
            />

            <TextField
              margin="dense"
              label="Dénomination"
              type="text"
              fullWidth
              variant="outlined"
              value={denomination}
              onChange={(e) => setDenomination(e.target.value)}
              error={!!personneMoraleErrors.denomination}
              helperText={personneMoraleErrors.denomination}
              size="small"
            />

            <TextField
              margin="dense"
              label="Numéro d'entreprise"
              type="text"
              fullWidth
              variant="outlined"
              value={companyNumber}
              onChange={(e) => setCompanyNumber(e.target.value)}
              size="small"
            />

            <TextField
              margin="dense"
              label="Adresse du siège social"
              type="text"
              fullWidth
              variant="outlined"
              value={headquarters}
              onChange={(e) => setHeadquarters(e.target.value)}
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleClosePersonneMoraleDialog}
            sx={{ color: 'text.secondary' }}
          >
            Quitter
          </Button>
          <Button 
            onClick={handleSavePersonneMorale}
            variant="contained"
            disabled={!personneMoraleQualite || !denomination}
          >
            {personneMoraleMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour ajouter/modifier un fait */}
      <Dialog open={openFaitDialog} onClose={handleCloseFaitDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          {faitMode === 'add' ? 'Ajouter un' : 'Modifier le'} fait
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isUndetermined}
                  onChange={(e) => setIsUndetermined(e.target.checked)}
                />
              }
              label="Fait non déterminé - PV subséquent"
              sx={{ mt: 1, mb: 2 }}
            />

            {!isUndetermined && (
              <>
                <FormGroup row sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isQualified}
                        onChange={(e) => setIsQualified(e.target.checked)}
                      />
                    }
                    label="Fait qualifié (mineur)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isTentative}
                        onChange={(e) => setIsTentative(e.target.checked)}
                      />
                    }
                    label="Tentative"
                  />
                </FormGroup>

                <TextField
                  margin="dense"
                  label="Fait"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={fait}
                  onChange={(e) => setFait(e.target.value)}
                  error={!!faitErrors.fait}
                  helperText={faitErrors.fait}
                  size="small"
                  multiline
                  rows={2}
                />

                <TextField
                  margin="dense"
                  label="Article"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={article}
                  onChange={(e) => setArticle(e.target.value)}
                  helperText="Facultatif"
                  size="small"
                />

                <ToggleButtonGroup
                  value={timeType}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue) setTimeType(newValue);
                  }}
                  fullWidth
                  size="small"
                  sx={{ mt: 2, mb: 1 }}
                >
                  <ToggleButton value="fixed">Heure fixe</ToggleButton>
                  <ToggleButton value="range">Durée</ToggleButton>
                </ToggleButtonGroup>

                {timeType === 'fixed' ? (
                  <TextField
                    margin="dense"
                    label="Date et heure"
                    type="datetime-local"
                    fullWidth
                    variant="outlined"
                    value={fixedDateTime}
                    onChange={(e) => setFixedDateTime(e.target.value)}
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      margin="dense"
                      label="Date et heure basse"
                      type="datetime-local"
                      fullWidth
                      variant="outlined"
                      value={rangeLowDateTime}
                      onChange={(e) => setRangeLowDateTime(e.target.value)}
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      margin="dense"
                      label="Date et heure haute"
                      type="datetime-local"
                      fullWidth
                      variant="outlined"
                      value={rangeHighDateTime}
                      onChange={(e) => setRangeHighDateTime(e.target.value)}
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Box>
                )}

                <TextField
                  margin="dense"
                  label="Adresse"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  error={!!faitErrors.address}
                  helperText={faitErrors.address}
                  size="small"
                  multiline
                  rows={2}
                />

                <TextField
                  margin="dense"
                  label="Pays"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  size="small"
                  helperText="Sera préfixé à l'adresse"
                />

                <TextField
                  margin="dense"
                  label="Destination du lieu"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                  helperText="Optionnel"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleCloseFaitDialog}
            sx={{ color: 'text.secondary' }}
          >
            Quitter
          </Button>
          <Button 
            onClick={handleSaveFait}
            variant="contained"
            disabled={!isUndetermined && (!fait || !address)}
          >
            {faitMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Typography variant="body2">
            Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleCloseDeleteDialog}
            sx={{ color: 'text.secondary' }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteItem}
            variant="contained"
            color="error"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de modification de rédaction */}
      <Dialog open={openRedactionDialog} onClose={handleCloseRedactionDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          Modifier les informations de rédaction
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box>
            <TextField
              margin="dense"
              label="Date et heure de début"
              type="datetime-local"
              fullWidth
              variant="outlined"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              error={!!redactionErrors.startDate}
              helperText={redactionErrors.startDate}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              margin="dense"
              label="Date de clôture"
              type="date"
              fullWidth
              variant="outlined"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              error={!!redactionErrors.endDate}
              helperText={redactionErrors.endDate}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleCloseRedactionDialog}
            sx={{ color: 'text.secondary' }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleSaveRedaction}
            variant="contained"
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de reprise d'encodage */}
      <Dialog open={openRepriseDialog} onClose={handleCloseRepriseDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          Reprendre d'un autre document
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box>
            {repriseError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {repriseError}
              </Alert>
            )}
            
            <TextField
              margin="dense"
              label="Numéro du PV"
              type="text"
              fullWidth
              variant="outlined"
              value={repriseNumber}
              onChange={(e) => setRepriseNumber(e.target.value)}
              error={!!repriseErrors.number}
              helperText={repriseErrors.number}
              size="small"
              inputProps={{ maxLength: 6 }}
              disabled={repriseLoading}
            />

            <TextField
              margin="dense"
              label="Année du PV"
              type="text"
              fullWidth
              variant="outlined"
              value={repriseYear}
              onChange={(e) => setRepriseYear(e.target.value)}
              error={!!repriseErrors.year}
              helperText={repriseErrors.year}
              size="small"
              inputProps={{ maxLength: 4 }}
              disabled={repriseLoading}
            />
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Éléments à reprendre:
            </Typography>
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repriseOptions.redaction}
                    onChange={handleRepriseOptionChange('redaction')}
                    disabled={repriseLoading}
                  />
                }
                label="Rédaction"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repriseOptions.service}
                    onChange={handleRepriseOptionChange('service')}
                    disabled={repriseLoading}
                  />
                }
                label="Service"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repriseOptions.intervenants}
                    onChange={handleRepriseOptionChange('intervenants')}
                    disabled={repriseLoading}
                  />
                }
                label="Intervenants"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repriseOptions.faits}
                    onChange={handleRepriseOptionChange('faits')}
                    disabled={repriseLoading}
                  />
                }
                label="Faits"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repriseOptions.personnesPhysiques}
                    onChange={handleRepriseOptionChange('personnesPhysiques')}
                    disabled={repriseLoading}
                  />
                }
                label="Personnes physiques"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repriseOptions.personnesMorales}
                    onChange={handleRepriseOptionChange('personnesMorales')}
                    disabled={repriseLoading}
                  />
                }
                label="Personnes morales"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repriseOptions.objets}
                    onChange={handleRepriseOptionChange('objets')}
                    disabled={repriseLoading}
                  />
                }
                label="Objets"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repriseOptions.vehicules}
                    onChange={handleRepriseOptionChange('vehicules')}
                    disabled={repriseLoading}
                  />
                }
                label="Véhicules"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repriseOptions.rubriques}
                    onChange={handleRepriseOptionChange('rubriques')}
                    disabled={repriseLoading}
                  />
                }
                label="Rubriques"
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleCloseRepriseDialog}
            sx={{ color: 'text.secondary' }}
            disabled={repriseLoading}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleReprise}
            variant="contained"
            disabled={repriseLoading || !repriseNumber || !repriseYear}
          >
            {repriseLoading ? 'Chargement...' : 'Reprendre'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de modification du type de PV */}
      <Dialog open={openTypeDialog} onClose={handleCloseTypeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', pb: 1 }}>
          Modifier le type de procès-verbal
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Box sx={{ pt: 1 }}>
            <RadioGroup
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value);
                if (e.target.value !== 'initial') {
                  setIndiceError('');
                } else {
                  validateIndice(indice);
                }
              }}
            >
              <FormControlLabel 
                value="initial" 
                control={<Radio />} 
                label="PV initial" 
              />
              <FormControlLabel 
                value="subsequent" 
                control={<Radio />} 
                label="PV subséquent" 
              />
            </RadioGroup>
            
            {reportType === 'initial' && (
              <TextField
                margin="dense"
                label="Indice (2 chiffres)"
                type="text"
                fullWidth
                variant="outlined"
                value={indice}
                onChange={(e) => {
                  setIndice(e.target.value);
                  validateIndice(e.target.value);
                }}
                error={!!indiceError}
                helperText={indiceError}
                inputProps={{ maxLength: 2 }}
                size="small"
                sx={{ mt: 2 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1 }}>
          <Button 
            onClick={handleCloseTypeDialog}
            sx={{ color: 'text.secondary' }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleSaveReportType}
            variant="contained"
            disabled={reportType === 'initial' && (!indice || !!indiceError)}
          >
            Modifier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification de copie */}
      <Snackbar
        open={copyNotification}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity="success" sx={{ width: '100%' }}>
          {`"${copiedText}" copié dans le presse-papier`}
        </Alert>
      </Snackbar>

      {/* Boutons Reprendre et Importer depuis ISLP */}
      <Box sx={{ 
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        mt: 2,
        zIndex: 10
      }}>
        <Box sx={{ 
          display: 'flex',
          gap: 1,
          px: 0.25
        }}>
          <Button
            variant="text"
            startIcon={<LoupeIcon />}
            onClick={handleOpenRepriseDialog}
            sx={{
              flex: 1,
              borderRadius: '12px',
              px: 2,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              color: isDark ? '#4fc3f7' : '#1565c0',
              backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(21, 101, 192, 0.08)',
              border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.4)' : 'rgba(21, 101, 192, 0.3)'}`,
              backdropFilter: 'saturate(160%) blur(8px)',
              boxShadow: isDark
                ? '0 4px 12px rgba(0,0,0,0.25)'
                : '0 3px 10px rgba(21, 101, 192, 0.15)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.25)' : 'rgba(21, 101, 192, 0.15)',
                boxShadow: isDark
                  ? '0 6px 16px rgba(0,0,0,0.35)'
                  : '0 4px 14px rgba(21, 101, 192, 0.2)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Reprendre
          </Button>
          <Button
            variant="text"
            startIcon={<FileUploadIcon />}
            onClick={handleOpenImportISLP}
            sx={{
              flex: 1,
              borderRadius: '12px',
              px: 2,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              color: isDark ? '#4fc3f7' : '#1565c0',
              backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(21, 101, 192, 0.08)',
              border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.4)' : 'rgba(21, 101, 192, 0.3)'}`,
              backdropFilter: 'saturate(160%) blur(8px)',
              boxShadow: isDark
                ? '0 4px 12px rgba(0,0,0,0.25)'
                : '0 3px 10px rgba(21, 101, 192, 0.15)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.25)' : 'rgba(21, 101, 192, 0.15)',
                boxShadow: isDark
                  ? '0 6px 16px rgba(0,0,0,0.35)'
                  : '0 4px 14px rgba(21, 101, 192, 0.2)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Importer depuis ISLP
          </Button>
        </Box>
      </Box>

      <ImportISLP
        open={openImportISLP}
        onClose={handleCloseImportISLP}
        report={reportData}
        reloadReportData={reloadReportData}
      />
    </Box>
  );
}

export default Elements;



